/**
 * The interactive player: a famous game, in 3D, at the visitor's own pace.
 *
 * WHAT THIS IS AND IS NOT
 * It is not a renderer. Every pixel on the board comes from
 * `animations/207-famous-chess-games/scene.js` — the same file the YouTube
 * captures are rendered from, imported unchanged, with no chess and no three.js
 * in this file at all. What this adds is the one thing the video cannot have:
 * somebody deciding when the next move happens.
 *
 * THE LOAD-BEARING FACT
 * The engine exposes `renderFrame(i)`, which draws frame `i` completely from
 * scratch — no wall clock, nothing carried over from the frame before. It exists
 * so `capture.js` can split a render across parallel browser workers and get
 * identical pixels back, and that property is exactly what a scrub bar needs: any
 * `i`, in any order, is a legal thing to ask for. So play is a `requestAnimation-
 * Frame` loop advancing `i` off real elapsed time, scrubbing is `i` set directly,
 * and "jump to move 22" is `i` looked up in the timeline. Nothing about the
 * renderer had to change.
 *
 * ONE SOURCE OF TRUTH, READ RATHER THAN RECOMPUTED
 * The timeline (when each move starts, how long it travels, where the phases
 * divide) is `ctx.timeline`, built by `timeline.js` inside the scene. This file
 * READS it off the live context instead of computing its own from the same
 * inputs. Two copies of that arithmetic is how a scrub bar comes to disagree with
 * the picture it is scrubbing — and the same argument that put `timeline.js` in
 * its own module in the first place (the publishing sheet's chapter marks) applies
 * with more force here, where the disagreement would be visible frame by frame.
 * `scripts/check-player.mjs` verifies it: the same frame, rendered here and
 * rendered by the capture path, has to come out the same pixels.
 *
 * THE ADDRESS BAR IS THE VISITOR'S, NOT THE ENGINE'S
 * The engine normally takes its parameters from the query string. Here the query
 * string belongs to the website — `?game=immortal&move=22` — so the parameters
 * are handed over through `window.__engineQuery` (see app mode in
 * `shared/engine.js`) and the URL stays a thing a person can read, share and
 * bookmark. Any engine parameter named in the visitor's URL is still passed
 * through, so `&board=ebony&finish=glass` works exactly as it does for a capture.
 *
 * THREE OF THE SCENE'S HELPERS ARE TURNED OFF, AND THEY ARE THE THREE THE PAGE
 * ALREADY SAYS ITSELF
 * `progressBar`, `moveList` and `card` are drawn INTO the video because a video
 * has no chrome. Here they are a second scrub bar, a second move list and a
 * second caption, each worse than the real one in the page around it — the card
 * worst of all, because it is TYPE laid into a picture at whatever size the
 * picture happens to be. A 1920-wide frame gives it 72px headlines; the same
 * frame on a phone is 360 across and the note under the move is four pixels
 * tall. It cannot be fixed by scaling either, because the thing it is competing
 * with for room is the board.
 *
 * The page has better places for all three lines. The whole annotation — the
 * move, its lead phrase and its note — is a row in the move list at every width,
 * highlighted as the game reaches it; the move and lead are also in `.at` under
 * the board on a wide window; and the title, the players and the result are in
 * the masthead and the pills. Same words in HTML, where they reflow, and the
 * board keeps the pixels.
 *
 * WHAT IS GONE ON A PHONE, AND IT IS WORTH KNOWING. `.at` is `display: none`
 * below 980px — the button row has more in it than fits, and the readout gives
 * up its room before anything you press does — so on a phone the move list is
 * now the ONLY place the current move is named. It names it well and it scrolls
 * under the docked board, but it is not beside the board the way the card was.
 * If that turns out to matter, the fix is a line of chrome and not this
 * parameter: give `.at` its own row under the scrub in the portrait layout,
 * where there is room for it, rather than painting type into the render again.
 *
 * Two lines have no home in the page at all, both on the closing card: what the
 * winner's material cost them (`winnerLine`) and the mating net read off the
 * final position (`describeNet`). The pills carry the result and the winner, so
 * what is lost is the colour commentary on the last frame only.
 *
 * Everything else the scene draws — the coordinates, the marks on the move, the
 * strikes, the score strip, the mating net — is the composition, and it stays.
 * It is all board rather than caption, so it scales with the board.
 *
 * THE VIEW IS THE VISITOR'S, AND IT HOLDS STILL
 * A video needs a camera that moves; somebody reading a position needs one that
 * does not. So the board is held at one angle by default and stays there — through
 * the whole game, through every phase — and the toolbar over the stage offers the
 * angles worth having outright: from White's side, from Black's, from the side, from
 * above. `Auto` hands the camera back to the scene, which is the video's own
 * direction: composed per phase and per big move in `games.js`, by a person, for
 * legibility. That is worth being one click away, and worth not being the default.
 *
 * HOW THE CAMERA IS TAKEN OVER, WHICH IS NOT BY FIGHTING IT
 * `placeCamera` in the scene reads one azimuth, one elevation and one zoom per
 * frame out of a baked table (`ctx.camTrack`), and derives everything else — the
 * distance that fits the board in this aspect ratio, where the writing sits, the
 * aim. So a held view is three numbers written into that table for the frame about
 * to be drawn, and the scene then places the camera itself. Nothing is overridden
 * afterwards and nothing is drawn twice.
 *
 * The first version of this did fight it: it re-placed the camera after
 * `renderFrame` had already run and rendered a second time, which cost a whole
 * extra frame for every frame the visitor was looking at, needed the pivot
 * reverse-engineered out of the camera pose, and could not survive playback at all.
 * Writing the inputs instead is smaller, exact, and free.
 *
 * Turning the scripted shots off is not enough to hold the board still, which is
 * worth knowing: `shots` only drops the PER-MOVE framing, and the per-PHASE
 * azimuth still swings the camera — 160 degrees of it, in the games that mate on
 * Black's back rank.
 *
 * THE SCENE'S PARAMETERS ARE THE VISITOR'S
 * All forty-two of them, in the View tab, built from `engine.specs` rather than
 * written out again here — the board and the men, wood or glass, the roughness of
 * the lacquer, how much of the room it reflects, where the camera sits and how
 * steady it is, what is drawn on the squares, how hard a capture lands. Four are
 * held back because this page owns them (the game, the stage's shape and length,
 * and the two helpers replaced by real chrome); everything else is a control. See
 * `view-panel.js`, and `setParams` in the engine for how a value gets in.
 *
 * PERFORMANCE IS MEASURED, NOT ASSUMED
 * A capture renders 1920x1080 on a graphics card with all night to do it. A
 * visitor's phone has neither, so the render resolution is fitted to the element
 * (never larger than it is displayed) and capped by a pixel budget, and if
 * playback is still dropping frames the tier steps down — fewer pixels and a
 * smaller shadow map — from what the page actually measures rather than from
 * guessing at the device. `?stats=1` shows the numbers.
 */

import { lengthFor } from '../../animations/207-famous-chess-games/timeline.js';
import { BOARDS, BY_ID, INDEX, watchUrl } from '../data/games-index.js';
import { playOnUrl } from '../play/from-here.js';
import { createVoice } from '../voice.js';
import { linesFor, linesForHeading, openingLines } from '/animations/207-famous-chess-games/narrate.js';
import {
  joinRoom, newRoomCode, ROOM_RE, savedName,
} from './together.js';
import { encodeShared } from '../data/shared-game.js';
import { drawQr } from '../play/qr.js';
import { publicUrl } from '../public-url.js';
import {
  isImported, load as loadImported, personName, records as importedRecords, registerShared, setChapters,
} from '../data/imported.js';
import { chaptersOf } from './review.js';
import { mountViewPanel, setSwatch } from '../view-panel.js';
import { openViewStore, truthy } from '../view-store.js';
import { onMode } from '../view-mode.js';
import { MOON, SUN, paintStage } from '../theme.js';
import { mountMaximise } from '../maximise.js';
import { mountBoardCamera, VIEWS as ANGLES, wrapDeg } from '../board-camera.js';
import { flatBoardFor } from '../flat-board.js';
import { mountStageSize, TIER_PIXELS, narrow, pickTier as tierForDevice } from '../stage-size.js';
import { mountNav } from '../nav.mjs';
import { mountInstall } from '../install.js';
import { count } from '../analytics.js';
import { note, drop } from '../resume.js';
import { mountSocial } from './social.js';
import { mountReview } from './review-ui.js';
import { mountCameraMenu as mountSharedCameraMenu } from '../camera-menu.js';
import { mountLeanLayout } from '../lean-layout.js';

// The sections, as a menu, into the slot the masthead holds for it. Before
// anything else this file does, and in particular before the wait for three.js:
// somebody who lands here and would rather be somewhere else should not have to
// wait for a WebGL context to find out where else there is.
//
// `here: 'watch'` and not nothing. This page is one game rather than a section,
// but it is the thing Watch exists to lead to, and the Watch item then points
// back at the library — which is exactly what the back-arrow it replaced did.
mountNav(document.getElementById('nav-slot'), { here: 'watch', base: '../' });
mountInstall(document.getElementById('install-slot'), { base: '../' });

const SCENE = '/animations/207-famous-chess-games/scene.js';

/** Where the scene lives, and the app's own query keys — never passed to it. */
const APP_KEYS = new Set([
  'move', 'ply', 'speed', 'stage', 'stats', 'play', 'view', 'video', 'app',
  // And the page's own settings, in here for the same reason the rest are: the
  // scene has never heard of them. Spelt out rather than taken from
  // `PAGE_SPECS`, which is declared further down the file and is still in its
  // temporal dead zone while this line runs.
  'plates',
  // A game carried in the link (see `data/shared-game.js`), and the tags a share
  // card's link is marked with for the Growth tab. Neither is the scene's.
  'shared', 'utm_source', 'utm_medium', 'utm_campaign',
]);

/**
 * The quality ladder.
 *
 * `pixels` is a budget on the framebuffer, not a resolution: the render is always
 * fitted to the size the canvas is actually displayed at (drawing 1920 wide into
 * an 800px box is 2.3x the pixels for no visible gain), and the budget is the
 * ceiling on top of that. `quality` is the scene's own parameter and is what the
 * shadow map costs — see `qualityTier` in scene.js.
 *
 * The tiers are only ever descended, and only on measured dropped frames: a
 * ladder that also climbs oscillates on any machine sitting near a boundary, and
 * an animation whose quality flickers is worse than one that is simply softer.
 */
const TIERS = [
  { name: 'high', quality: 'high', pixels: TIER_PIXELS[0] },
  { name: 'medium', quality: 'medium', pixels: TIER_PIXELS[1] },
  { name: 'low', quality: 'low', pixels: TIER_PIXELS[2] },
];

const SPEEDS = [0.5, 1, 2];

/**
 * Seconds per half-move, which is what this page sets instead of a length.
 *
 * THE LENGTH IS THE GAME'S, NOT THE PAGE'S. The scene takes a `Length` and
 * divides the game into it — right for a video, which is commissioned at three
 * minutes and fits whatever game it is given, and wrong here. The library runs
 * from 4 half-moves to 82, so one length for all of them is a mate in two read
 * at forty seconds a move and a 41-move game read at under two: the same three
 * minutes, and neither of them the pace anybody wanted. So the pace is the
 * constant and the length falls out of it — `lengthFor` in `timeline.js`, which
 * adds the intro and the hold on the mate back on.
 *
 * 4.5, WHICH IS SLOWER THAN THE VIDEOS, ON PURPOSE. It was 3.2 — the video's own
 * pace, a 49-half-move score dividing three minutes less the bookends — on the
 * argument that a visitor arriving from a video should watch what they were
 * watching. Watched on the page, 1× felt hurried: a video is watched, and this
 * page is READ, with the move list and the notes beside the board competing for
 * the same seconds. So the page takes 40% longer over every move and 1× is the
 * pace for reading along; 2× is still there for the video's. The library's median
 * game (40 half-moves) now runs about 3:20.
 *
 * `emphasis` then divides those seconds unevenly, which is the whole point of it:
 * a move the game is remembered for gets two to three times this, a quiet one
 * less. Of either, only the first `travel` seconds at most are the piece moving
 * (see `APP_DEFAULTS`) — the rest is it standing on its square, which is the part
 * somebody reading a position actually needs.
 *
 * `?duration=` still wins over it, and `?video=1` turns it off with the rest of
 * the page's defaults.
 */
const PLY_SECONDS = 4.5;

/**
 * The angles worth having as a button, in the scene's own terms: azimuth 0 is
 * behind White, 180 behind Black, and elevation is degrees up off the table.
 *
 * `angled` is the scene's own opening key (a person's choice of establishing shot)
 * and so the default. `top` stops short of 90 on purpose: at exactly overhead the
 * camera's up vector and its view direction are parallel and `lookAt` has no
 * defined roll, which is a shot that flips depending on floating-point luck.
 */
/**
 * The toolbar: `Auto` in front of the five angles every board in `chess-app/`
 * offers.
 *
 * The five live in `../board-camera.js` now, and are the same five to the degree
 * on the play pages and the puzzles page. `Auto` is this page's own, and it is
 * the one entry that cannot travel: it hands the camera back to the VIDEO'S OWN
 * composed shots, and only a page with a shot list has anywhere to hand it. It is
 * also the only thing that puts `held` back to null — see `scene` in that file.
 */
const VIEWS = {
  auto: {
    label: 'Follow the game',
    scene: true,
    title: 'Give the camera back to the game — the angles the video is shot from',
  },
  // The same five as every other board page, with this page's words for them:
  // they are a menu here (see `mountCameraMenu`), and a menu of "¾, White,
  // Black, Side, Top" says nothing to somebody who has not used it before.
  ...(() => {
    const words = {
      angled: 'Angled',
      white: 'White’s side',
      black: 'Black’s side',
      side: 'From the side',
      top: 'From above',
    };
    return Object.fromEntries(Object.entries(ANGLES).map(([name, preset]) => [name, {
      ...preset,
      label: words[name] || preset.label,
      title: preset.title || `Look from ${(words[name] || preset.label).toLowerCase()}`,
    }]));
  })(),
};

/**
 * What this page asks the scene for that a capture does not.
 *
 * The three helpers it replaces with real chrome, and then the three that MOVE
 * the camera: the scripted shots, the drift that keeps a video from looking like
 * a still, and the pan toward each move. All three are right for a video and
 * wrong for a board somebody is reading, and all six remain in the View panel
 * for anyone who wants the video's behaviour back — `card` under Helpers as
 * "Move card", which is also what `?card=1` asks for.
 *
 * `?video=1` turns this whole object off — the page then renders exactly what the
 * upload renders, which is what `scripts/check-player.mjs` compares against and
 * what makes that comparison self-maintaining: another default added here needs no
 * change to the check.
 */
const APP_DEFAULTS = {
  // SILENT, BECAUSE THIS PAGE ALREADY HAS A VOICE. The scene narrates by default
  // now — see `narration` in its params — and the speaker button beside the move
  // list reads the same sentences out of `narrate.js`, at the pace a visitor is
  // stepping through at, while the scene's voice-over reads them off the video's
  // clock. Both at once is the same paragraph twice, a beat apart, and no visitor
  // would guess which control turned which one off. `?video=1` drops this with the
  // rest, so the upload still talks.
  narration: '0',
  // The piece itself moving, at most a second and a half rather than the video's
  // one: at `PLY_SECONDS` a quiet move's slot is long enough to spend it, and a
  // piece that crosses the board in a second, then stands for three, reads as a
  // jump. `?video=1` drops it with the rest and the video's travel comes back.
  travel: '1.5',
  // AND THEREFORE NO COLUMN BESIDE THE BOARD. On a wide frame the scene gives a
  // third of the width to the card, the score and the bar and the other two
  // thirds to the board — see `panel` and `frameLayout` in the scene. That is the
  // right answer for a video, which has no chrome, and the wrong one here twice
  // over: the three things the column carries are the three this page turns off
  // two lines below, so the column would be empty, and paying for it costs the
  // board a third of a canvas the page has already sized for the board alone. The
  // move list, the scrub bar and the readout are in the page, beside the canvas,
  // in HTML that reflows.
  //
  // NOT `live` in the scene, so this one costs a rebuild rather than a repaint —
  // which is why it is here in the defaults a page opens with rather than
  // something the toolbar flips. `?video=1` drops it with the rest and the
  // upload's layout comes back, which is what `scripts/check-player.mjs`
  // compares against.
  panel: '0',
  moveList: '0',
  progressBar: '0',
  // The corner card: the move and its note in the middle of the game, the title
  // and the players before it starts, the result and the mating net after. Every
  // line of it is somewhere in the page already — see the module note above — and
  // in the picture it is type that cannot reflow, over the one thing on the page
  // that wants the room.
  card: '0',
  // And the players' names as a thing on the table instead — two plaques at the
  // ends of the board, which is the one place a name can be that says WHICH MEN
  // it belongs to. On in the scene now as well; said here too so this page keeps
  // them if the scene's default moves again. See `buildNames` in the scene, and
  // `paintPlates` below for what it costs the page's own nameplates.
  names: '1',
  // AND NO WEBSITE MARK IN THE CORNER. The scene draws `chesszz.com` top right
  // because a video is watched where this project has no other way to say so —
  // see the header of `animations/207-famous-chess-games/scene.js`. This page IS
  // chesszz.com, so the mark would be the address telling a visitor where they
  // already are, over the one thing on the page that wants the room. `?video=1`
  // still renders it, which is what `scripts/check-player.mjs` compares against.
  site: '',
  // THE CLUB STAUNTON MEN RATHER THAN THE CARVED FIGURES: a thumbnail has to earn a
  // click, and a board somebody is reading move by move has to be READ — a
  // Staunton knight at a glance is a knight where a carved horse's head is a shape
  // to work out. The scene reasons its way to the same default now, so this line
  // changes nothing by itself; it stays because the argument for it here is this
  // page's own, and a video that went back to the figures should not take this
  // board with it. The figures are one click away — the header strip has both —
  // and a visitor who picks them is remembered, since the store outranks this
  // list.
  men: 'club',
  // A LINE ROUND EACH MAN, asked for here for the same kind of reason as the
  // Staunton men: a phone reading this board is a few hundred pixels wide against
  // a capture's 1080, and a pale man on a pale square — worst of all in the marble
  // and glass finishes — has no silhouette left at that size. The scene draws it
  // by default now as well, on its own evidence, so `?video=1` no longer takes the
  // line away with the rest of this object; what that flag still means is
  // unchanged — the upload renders the scene's own answer, and the check in
  // `scripts/check-player.mjs` compares like with like.
  outline: 'true',
  // SOUND ON. The scene knocks a piece down on every move and rings a bell on
  // check — see `sound.js`. This used to be '0', on the argument that a page
  // makes noise when a visitor asked it to and not before; it is '1' now because
  // a game of chess with no sound is a silent film, and the button that turns it
  // off is the first one in the panel and says "Sound" on it.
  //
  // The rule it looked like it was breaking is enforced by the browser anyway,
  // not by this line: an AudioContext made outside a gesture starts suspended,
  // and `sound.js` hangs its resume off the first pointerdown, keydown or
  // touchstart. So nothing sounds on arrival either way. What changes is that
  // the first click is no longer a click on a button nobody could name.
  sound: '1',
  shots: 'false',
  drift: '0',
  follow: '0',
};

// After a move's travel has finished, how much longer a step keeps running.
//
// A move is not over when the piece arrives: it lands with an overshoot, a taken
// piece is still falling (its tumble is on the clock, not on the move's progress,
// so a struck queen may still be in the air), and the strike's sparks and
// shockwave have their own fifth of a second. Stopping the instant the travel
// ends cuts all of that off mid-flight. Capped at the move's own slot so a step
// never runs into the next move.
const BEAT_TAIL = 0.4;
// How much of the outstanding orbit movement is spent per frame at 60fps — the
// same quantity as OrbitControls' `dampingFactor`, and the whole of how a drag
// feels. Theirs defaults to 0.05, which suits a scene being toured; this is
// tighter because a chessboard is a thing being inspected, and a third of a second
// of drift after the hand stops reads as the page being slow rather than smooth.
// Higher is stiffer; 1 is no damping at all, which is where this started.
const ORBIT_DAMPING = 0.11;
// Below this share of the frames it should be drawing, playback is judged to be
// dropping frames and the tier steps down. 0.8 of 30fps is 24 — the point where
// a piece sliding across a board stops looking like it is sliding.
const DROP_RATIO = 0.8;

const $ = (id) => document.getElementById(id);
const clamp = (x, a, b) => Math.min(b, Math.max(a, x));

// ------------------------------------------------- the visitor's own games

/**
 * A PGN somebody imported on the library page is a game this page can open.
 *
 * BEFORE EVERYTHING, and for two reasons that both look like the picture is fine
 * until they are not. `gameId` below resolves `?game=` against `BY_ID`, so a link
 * to an imported game would fall back to the Immortal Game; and `scene.js`, which
 * is imported further down, builds its `Game` parameter's options at import time,
 * so a game registered afterwards is one the engine has never heard of. Both are
 * the ordering `play/table.js` keeps for the live game, for the same reason.
 *
 * Top-level await, and `load()` cannot throw or hang — see `data/imported.js`.
 */
await loadImported();

// ------------------------------------------------------------------- the URL

const app = new URLSearchParams(location.search);
// A game the library does not have is a stale link, and the scene would fall
// back to the first game anyway — agreeing with it here keeps the picker, the
// URL and the picture naming the same game.
//
// A game somebody shared is registered first, and wins over `?game=`: a share
// link is the one kind of link that brings its own game.
const sharedId = app.get('shared') ? await registerShared(app.get('shared')) : null;
const gameId = sharedId || (BY_ID.has(app.get('game')) ? app.get('game') : INDEX[0].id);
// WHICH game, which the page view alone cannot say: the watch page is one URL for
// all hundred, and the id is in the query rather than the path. Reported here
// and not in `analytics.js`, because reading a query string is exactly what that
// file will not do — a query is where a name or an email ends up when somebody
// pastes a link, and a rule with no exceptions is the only kind worth having.
count('watch-game', gameId);
// And the public count of the same thing, with the reactions row it comes back
// with. Not awaited: the board does not wait on a number. See `social.js`.
mountSocial({ gameId, imported: isImported(gameId) });
const wantStats = app.get('stats') === '1';
const nativeStage = app.get('stage') === 'native';
// "Render what the video renders": no app overrides, and the scene's own camera.
const videoMode = app.get('video') === '1';

// -------------------------------------------------- the visitor's own settings

/**
 * WHAT SOMEBODY CHOSE, KEPT FOR THE NEXT TIME THEY OPEN THE APP — AND FOR THE
 * NEXT PAGE OF IT THEY OPEN.
 *
 * `../view-store.js` is the whole of it, and its header is where the argument
 * is: why the address bar is not the place for a settings list, why the store
 * has to be synchronous, and what beats what on the way in. It moved out of this
 * file when the View panel stopped being this page's — the two play boards, the
 * lessons, the puzzles and the guessing game all show it now, and a store per
 * page would mean choosing the glass men six times.
 *
 * What is still this page's is the two arguments below: `APP_DEFAULTS`, which is
 * what THIS page shows when nobody has said otherwise, and `videoMode`, which
 * means "render what the upload renders" and so ignores a visitor's taste in
 * chess sets entirely. That is also what keeps `scripts/check-player.mjs`
 * honest, since it drives this page in exactly that mode.
 */
const store = openViewStore({ on: !videoMode, defaults: APP_DEFAULTS });
const prefs = store.prefs;

/**
 * One parameter, as it now stands, kept or dropped — the store's `remember`,
 * with the one thing this page has that it does not: a setting of the PAGE's
 * carries its own reader, because there is nothing in `engine.params` to read.
 * See `PAGE_SPECS`.
 */
function rememberPref(key) {
  const spec = PAGE_SPECS[key] || engine.specs[key];
  if (!spec) return;
  store.remember(key, spec, PAGE_SPECS[key] ? spec.value() : engine.params[key]);
}

/** What this page shows when nobody has said otherwise. See the store. */
const pageDefault = (key, spec) => store.defaultOf(key, spec);

let tierIx = pickTier();
let stageIsSlow = false;

/**
 * Where to start on the ladder.
 *
 * A guess, and the only guess here: it decides what the FIRST build looks like,
 * before anything can have been measured. A phone gets the middle tier rather
 * than the top one because arriving soft and staying there is better than
 * arriving beautiful, dropping frames through the first ten seconds of the game
 * and then rebuilding.
 */
function pickTier() {
  // A native-sized stage is a capture, not a visit: it renders exactly what the
  // video renders, at the top of the ladder, whatever the machine is.
  return nativeStage ? 0 : tierForDevice();
}

// --------------------------------------------------------------- boot the scene

const boot = $('boot');
// Which game is coming, while it comes. The scene builds in well under a second
// on a desktop and noticeably longer on a phone, and a wait that names what it is
// waiting for is a different wait.
$('boot-note').textContent = BY_ID.get(gameId).title;

/**
 * The parameters the scene is built with.
 *
 * Precedence: whatever the visitor's URL names wins (so a deep link can dial the
 * set, the finish or the emphasis exactly as `capture.js --set` does), then the
 * app's own overrides, then the scene's defaults — which are the same defaults
 * every published video was rendered at, and that is deliberate: what a visitor
 * arrives at is what they may have come from.
 *
 * `Length` is the one exception, and it is the page's rather than the video's
 * because a page is not three minutes long. See `watchLength`.
 */
function engineQuery() {
  const q = new URLSearchParams();
  for (const [key, value] of app) if (!APP_KEYS.has(key)) q.set(key, value);
  q.set('app', '1');
  q.set('game', gameId);
  if (!videoMode) {
    // What this visitor last chose, under anything this URL names and over the
    // page's own defaults — see `into` in the store. The page's own query keys
    // are not the scene's business and never go through here.
    store.into(q, APP_KEYS);
    for (const [key, value] of Object.entries(APP_DEFAULTS)) {
      if (!q.has(key)) q.set(key, value);
    }
    // Not in APP_DEFAULTS because it is not a constant: it is this game's own
    // length, off its own ply count. See `PLY_SECONDS`.
    if (!q.has('duration')) q.set('duration', String(watchLength()));
  }
  if (!q.has('quality')) q.set('quality', TIERS[tierIx].quality);
  // Landscape even on a phone, and not for want of a portrait composition — the
  // scene has a good one, and `?orientation=portrait` still gets it. It is that
  // a 9:16 stage leaves no room in the page for the move list and the controls,
  // which are the reason to be here rather than on YouTube.
  if (!q.has('orientation')) q.set('orientation', 'landscape');
  return q.toString();
}

/**
 * How long this game runs, in seconds: a pace per half-move, turned into the
 * `Length` the scene wants.
 *
 * The ply count comes off the library row rather than out of the scene, because
 * this is needed BEFORE the scene is imported — the length is a parameter it is
 * built with, and `totalFrames` is fixed once it is. `moves` is the score as one
 * token per half-move, so its length is the ply count with no board to replay
 * (see `games-index.js`), and an imported PGN gets a row of the same shape.
 *
 * Whole seconds because the engine's `Length` is stepped by 1 and would snap it
 * anyway, and no ceiling here on purpose: the scene declares `maxDuration`, the
 * engine clamps to it, and a second copy of that number in this file is a second
 * copy to be wrong. A game long enough to hit it (273 half-moves at this pace,
 * against 82 for the longest game in the library) is back to dividing a fixed
 * length — the old behaviour, at the one size where there is no alternative, and
 * reachable only by an imported PGN of a marathon.
 */
function watchLength() {
  return Math.round(lengthFor(BY_ID.get(gameId).plies * PLY_SECONDS));
}

window.__engineQuery = engineQuery();

let engine;
try {
  await import(SCENE);
  await waitFor(() => window.__READY === true, 30000);
  engine = window.__engine;
} catch (err) {
  bootFailed(err);
  throw err;
}

function waitFor(test, timeout) {
  return new Promise((resolve, reject) => {
    const started = performance.now();
    const poll = () => {
      if (test()) return resolve();
      if (performance.now() - started > timeout) return reject(new Error('the scene never became ready'));
      requestAnimationFrame(poll);
    };
    poll();
  });
}

function bootFailed(err) {
  boot.innerHTML = `<div>
    <div class="title">The board did not load</div>
    <div>${escapeHtml(err?.message || String(err))}</div>
    <code>npm run dev &nbsp;→&nbsp; http://localhost:5115/chess-app/</code>
  </div>`;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

// ------------------------------------------------------------------- the scene

const meta = window.__meta;
const fps = meta.fps;
const canvas = document.getElementById('stage');
const stageBox = $('stage-box');
stageBox.appendChild(canvas);
// The 2D board, drawn in SVG over the canvas — see `flat-board.js`. It hooks
// `renderFrame`, so every frame `draw` asks for is drawn there while 2D is on.
flatBoardFor(engine, canvas);

// Everything below reads these through a function rather than holding the object,
// because `setStage` rebuilds the scene and hands back new ones. A cached
// `ctx.timeline` across a resize is a scrub bar measuring a game that no longer
// exists.
const ctx = () => engine.ctx;
const tl = () => engine.ctx.timeline;
const plies = () => engine.ctx.game.plies;
const meta207 = () => engine.ctx.g;

const total = ctx().totalFrames;
const lastFrame = total - 1;
// Whole moves, which is what a score is numbered in — the last ply's move number
// rather than half the ply count, since a game can end on White's.
const moveTotal = plies().at(-1).moveNo;

// ------------------------------------------------------------------- the page

const els = {
  title: $('title'), subtitle: $('subtitle'), pills: $('pills'), blurb: $('blurb'),
  blurbToggle: $('blurb-toggle'),
  clock: $('clock'), duration: $('duration'), phaseStrip: $('phase-strip'),
  track: $('track'), fill: $('fill'), knob: $('knob'), rail: $('rail'),
  play: $('play'), playIcon: $('play-icon'), playLabel: $('play-label'),
  start: $('start'), prev: $('prev'), next: $('next'), end: $('end'), speeds: $('speeds'),
  status: $('status'), list: $('move-list'), moves: $('moves'), hint: $('hint'),
  atMove: $('at-move'), atLead: $('at-lead'), phaseJump: $('phase-jump'),
  views: $('views'), theme: $('theme'), themeIcon: $('theme-icon'),
  view: $('view'), tabMoves: $('tab-moves'), tabView: $('tab-view'),
  tabMovesN: $('tab-moves-n'), tabViewN: $('tab-view-n'),
  quick: $('quick'),
  readLink: $('read-link'),
  playOn: $('play-on'),
  voice: $('voice'), voiceIcon: $('voice-icon'),
  sound: $('sound'), soundIcon: $('sound-icon'),
  together: $('together'), togetherHow: $('together-how'),
  togetherLink: $('together-link'), togetherCopy: $('together-copy'),
  togetherShare: $('together-share'), togetherQrBox: $('together-qr-box'),
  togetherQr: $('together-qr'), togetherPeers: $('together-peers'),
  togetherLeave: $('together-leave'), togetherNote: $('together-note'),
  togetherTitle: $('together-title'), togetherName: $('together-name'),
  lessonBox: $('lesson-box'), lessonSay: $('lesson-say'), lessonStart: $('lesson-start'),
  lessonBig: $('lesson-big'), lessonOffer: $('lesson-offer'), lessonOffers: $('lesson-offers'),
  lessonRoster: $('lesson-roster'), lessonMine: $('lesson-mine'), lessonMineRow: $('lesson-mine-row'), lessonBigView: $('lesson-big-view'),
  lessonBigQr: $('lesson-big-qr'), lessonBigLink: $('lesson-big-link'),
  findOpen: $('find-open'), palette: $('palette'), paletteInput: $('palette-input'),
  paletteBody: $('palette-body'), paletteN: $('palette-n'), paletteVerb: $('palette-verb'),
};

// ------------------------------------------------------------------ the voice

/**
 * The game, read aloud as the playhead crosses each move.
 *
 * WHY THIS PAGE AND NOT ONLY THE BEGINNER'S ONE. `voice.js` was written for the
 * lessons, where the difficulty is that a beginner cannot look at the lit squares
 * and read the sentence about them at the same time. The same difficulty is here
 * one level up — the board is the thing to watch and the annotation is beside it
 * in a panel — and there is a second audience this page has that the lessons do
 * not: everybody who cannot see the board at all. Nobody has made chess history
 * listenable. See `narrate.js`.
 *
 * SET UP HERE, BESIDE `els`, RATHER THAN AT THE FOOT OF THE FILE, because
 * `reflect` narrates and `reflect` runs during boot. A `const voice` declared
 * after the first draw is a temporal dead zone and a page that does not come up.
 *
 * The key is `voice.js`'s own, shared with the beginner's page on purpose: "read
 * things out to me" is one preference about a person, not two facts about two
 * pages, and somebody who turned it on to learn the moves has said what they want.
 */
const VOICE_KEY = 'immortal-voice';
const voice = createVoice({ onChange: paintVoice });

if (voice.available) {
  els.voice.hidden = false;
  $('voice-key').hidden = false;
  // On unless it was turned OFF: see the `aloud` row in `view-mode.js`, which
  // writes the 'off' this reads. A missing key is somebody who has never
  // answered, and the answer this page gives them is yes.
  try { voice.on = localStorage.getItem(VOICE_KEY) !== 'off'; } catch { /* private mode */ }
  paintVoice({ on: voice.on, speaking: false });
}

els.voice.addEventListener('click', () => setVoice(!voice.on));

/**
 * Is the board making a noise?
 *
 * `sound` IS NOT A TOGGLE. The scene declares it `{ type: 'range', min: 0, max:
 * 1, step: 0.05 }` — a volume — so `engine.params.sound` is the NUMBER 1, and
 * both places below used to ask `truthy(engine.params.sound)`. `truthy` only
 * recognises the strings '1', 'true', 'on' and 'yes', so it answered "off"
 * however loud the board was.
 *
 * That made the button one-way: the first click read "off", set '1' and painted
 * it on; the second click read "off" again, set '1' again, and nothing moved.
 * Nobody caught it because the default was '0' and the first click was the only
 * one most people ever made — the second is the one that hurts, and it is the
 * one somebody makes when the sound has surprised them.
 *
 * `Number` rather than `truthy` because a volume is a number, and it takes the
 * URL's string spelling with it: `?sound=0.5` in a shared link is half volume
 * and counts as on.
 */
const soundOn = () => Number(engine.params.sound) > 0;

paintSound(soundOn());
els.sound?.addEventListener('click', toggleSound);

function paintSound(on) {
  const button = els?.sound;
  if (!button) return;
  button.setAttribute('aria-pressed', on ? 'true' : 'false');
  button.title = on ? 'Mute board sound (m)' : 'Play board sound (m)';
  if (els.soundIcon) {
    els.soundIcon.innerHTML = '<path d="M2.5 6.2h2.2L8 3.4v9.2L4.7 9.8H2.5z"></path>'
      + (on
        ? '<path d="M10.4 5.9a3 3 0 0 1 0 4.2"></path><path d="M12.4 4.1a5.6 5.6 0 0 1 0 7.8"></path>'
        : '<path d="M10.8 6.2l3.2 3.6M14 6.2l-3.2 3.6"></path>');
  }
}

function toggleSound() {
  const current = soundOn();
  const next = current ? '0' : '1';
  engine.setParams({ sound: next });
  rememberPref('sound');
  paintSound(!current);
}

function setVoice(want) {
  if (!voice.available) return;
  voice.on = want;
  try { localStorage.setItem(VOICE_KEY, want ? 'on' : 'off'); } catch { /* private mode */ }
  // The same event the beginner's page reports, and the same reason: a feature
  // nobody turns on is a button in the way, and `off` arriving seconds after `on`
  // says something different again from `off` never arriving.
  count('voice', want ? 'on' : 'off');
}

/**
 * The speaker icon, in its three states.
 *
 * The waves are drawn and hidden rather than added and removed, so the icon is
 * one shape at one width whether it is speaking or not — a glyph that grew waves
 * would nudge the fullscreen button along beside it every time a sentence began.
 * `controls.css` fades them.
 */
function paintVoice({ on, speaking }) {
  const button = els?.voice;
  if (!button) return;
  button.setAttribute('aria-pressed', on ? 'true' : 'false');
  button.classList.toggle('saying', Boolean(on && speaking));
  button.title = on ? 'Stop reading aloud (v)' : 'Read the game aloud (v)';
  // A bubble, not a speaker: `paintSound` owns the speaker, and when both were
  // off the row drew the same crossed speaker twice. The lines inside keep the
  // `wave` class, so they still pulse while a sentence is being said.
  els.voiceIcon.innerHTML = '<path d="M2.6 3.2h10.8v6.2H6.4L3.4 12.2V9.4H2.6z"></path>'
    + (on
      ? '<path class="wave" d="M5.2 5.4h5.6"></path>'
        + '<path class="wave" d="M5.2 7.3h3.4"></path>'
      : '<path d="M4 11.6L12.8 2.8"></path>');
}

/**
 * Say the move the playhead has just reached.
 *
 * Called whether or not the voice is on: `speak` remembers what it was last
 * asked for even while silent, so turning it on says the move being looked at
 * rather than nothing until the next one. That is the whole reason `voice.js`
 * keeps `last`.
 */
function narrate(k) {
  if (!voice.available) return;
  voice.speak(narrationLines(k));
}

/** What `narrate` says for half-move `k` — apart, so a check can ask. */
function narrationLines(k) {
  const g = meta207();
  if (k < 0) return openingLines(g);
  // A move nobody wrote about is read with what the review made of it — which
  // is how "listen to your own game" says anything more than the moves.
  const m = review?.result()?.moves[k];
  const hard = m?.hard ? ` How hard ${m.best || m.hard.san} was to find: ${m.hard.text}` : '';
  const note = g.notes[k] || (m?.level ? { lead: m.word, note: `${m.reason}${hard}` }
    : m?.only ? { lead: 'The only good move here, and it was found', note: m.hard?.text || '' } : null);
  const lines = linesFor(plies()[k], note);
  // A new chapter is announced as it starts, the way a reader says a heading.
  const at = g.phases.findIndex((p, i) => k === (i === 0 ? 0 : g.phases[i - 1].through + 1));
  if (at >= 0 && g.phases.length > 1) lines.unshift(...linesForHeading(g.phases[at].title));
  return lines;
}

// ------------------------------------------------------- watching it together

/**
 * Two people, one playhead.
 *
 * WHAT IS MISSING EVERYWHERE ELSE, and how little it costs here, is in the header
 * of `together.js`. What is in this file is only the page's half: report where the
 * playhead is when it moves, go where the room says, and one sheet with a link in
 * it.
 *
 * THE ROOM IS IN THE URL as `?with=`, which is what makes the link a link — the
 * person you send it to opens the game you were watching, in the room you were
 * watching it in, without anybody having pressed anything. `syncUrl` keeps every
 * parameter it does not own, so the room survives every step through the game.
 *
 * A ROOM SURVIVES CHANGING GAMES, which is the reason `at` carries a game id at
 * all. Somebody in a room who opens a different game takes the room with them:
 * everybody else's page navigates, and the two of them are watching the new game
 * together. Without it the first click on another game would silently end the
 * session for one person and leave the other talking to nobody.
 */
let together = null;
let room = null;

/**
 * Whether this page load is taking the room somewhere rather than joining it.
 *
 * Read once and removed from the address bar immediately: it is an instruction
 * for this page load, not a property of the link, and a copied URL carrying it
 * would drag a room to this game every time anybody opened it. `together.js`
 * says why the distinction has to exist at all.
 */
const leading = app.get('lead') === '1';
if (leading) {
  const url = new URL(location.href);
  url.searchParams.delete('lead');
  history.replaceState(null, '', url);
}

/** The room in the URL, if there is a good one. */
function roomFromUrl() {
  const asked = app.get('with');
  return asked && ROOM_RE.test(asked) ? asked.toLowerCase() : null;
}

/** Put the room in the address bar, or take it out. */
function markRoomUrl(code) {
  const url = new URL(location.href);
  if (code) url.searchParams.set('with', code);
  else url.searchParams.delete('with');
  history.replaceState(null, '', url);
}

/**
 * Where this page is, said to the room.
 *
 * Called from `reflect` — so every step, scrub, jump and click on the move list
 * reports itself through the one function the whole page already routes those
 * through — and from `play` and `pause`, which change the third field without
 * changing the first two. `together.at` drops anything that is not news, so
 * calling it more often than necessary costs nothing.
 */
function reportPlayhead() {
  if (!together) return;
  together.at({ game: gameId, ply: shownPly, playing, shared: sharedCode() });
}

/**
 * A visitor's own game, as a `?shared=` code, so the others in the room can open
 * it too: imported games live in one browser only. The code this page was
 * opened with, when it was opened with one — a follower compares codes, so the
 * same game must keep the same code. Null for a famous game, which everybody has.
 */
let sharedMemo;
function sharedCode() {
  if (!isImported(gameId)) return null;
  if (sharedMemo === undefined) {
    const g = meta207();
    sharedMemo = app.get('shared') || encodeShared({
      white: g.white, black: g.black, date: g.date, event: g.event, result: g.result,
      moves: plies().map((p) => p.san),
    });
  }
  return sharedMemo;
}

/**
 * Go where the room went.
 *
 * A DIFFERENT GAME IS A NAVIGATION and not a rebuild, which is worth being
 * explicit about: this page resolves its game once, at import, and builds a scene
 * around it. Loading another one in place would be most of the work of the page
 * again. The room goes along in the URL, so what the visitor sees is the game
 * changing and the session carrying on.
 */
function goWhereTheySay({ game, ply, playing: theyPlay, shared }) {
  // A shared game has a different id in each browser that registers it, so the
  // code is what says whether it is the one already open.
  const same = game === gameId || (shared && shared === app.get('shared'));
  if (game && !same) {
    // No `lead` on this one: this page is following, and the room already says
    // where it is. The move is carried so the new page opens near the right place
    // while it waits for the room to tell it exactly.
    const url = new URL('player.html', location.href);
    url.searchParams.set('game', game);
    if (shared) url.searchParams.set('shared', shared);
    url.searchParams.set('with', room);
    if (ply >= 0) url.searchParams.set('move', String(Math.floor(ply / 2) + 1));
    location.href = url.toString();
    return;
  }
  // -1 is the opening card, which `goToPly` cannot express: it clamps to the
  // first move. A seek to frame 0 is the same place.
  if (ply < 0) { pause(); seek(0); } else goToPly(ply, { animate: false });
  if (theyPlay) play(); else pause();
}

/**
 * A link to another game, with the room brought along.
 *
 * The one place a person changes game from inside the player is the search
 * palette, and doing it while watching with somebody should mean "let us look at
 * this one now" rather than "goodbye". `lead` is what tells the arriving page to
 * announce rather than follow — see `together.js`.
 *
 * Returns the link unchanged when there is no room, which is every ordinary
 * visit.
 */
function takeRoomTo(href) {
  if (!room) return href;
  const url = new URL(href, location.href);
  url.searchParams.set('with', room);
  // A student in a lesson does not take the room anywhere: the page arrives,
  // follows the coach, and is back in the lesson.
  if (!lesson || lesson.host) url.searchParams.set('lead', '1');
  return url.toString();
}

// ------------------------------------------------------------ a lesson

/**
 * A LESSON (Phase 3b): the same room with one person leading — see
 * `play/lesson.js` for the rules and `together.js` for the host's key. Here:
 * the name, the button that starts one, the lens the host sets for everybody,
 * who is here, and the games students offer the coach.
 */
let lesson = null;
let offers = [];
/** The review and the forces button are mounted further down; a lens waits for them. */
let lessonReady = false;
let pendingLens = null;
let applyingLens = false;
let reviewRunning = false;
/** A student's own games, newest first, once looked up: `[{ id, label, shared }]`. */
let mine = null;

async function loadMine() {
  mine = [];
  const rows = await importedRecords();
  rows.sort((a, b) => (a.fileId < b.fileId ? 1 : a.fileId > b.fileId ? -1 : a.n - b.n));
  mine = rows.slice(0, 50).map((r) => {
    const white = personName(r.headers?.White);
    const black = personName(r.headers?.Black);
    const date = String(r.headers?.Date || '').slice(0, 4).replace(/\?/g, '');
    return {
      id: r.id,
      label: `${white} vs ${black}${date ? `, ${date}` : ''}`,
      shared: encodeShared({
        white, black, date, event: r.headers?.Event, result: r.result, moves: r.moves,
      }),
    };
  });
  els.lessonMine.innerHTML = mine.map((g, i) => `<option value="${i}">${escapeHtml(g.label)}</option>`).join('');
  paintLesson();
}

function applyLens(lens) {
  if (!lens) return;
  if (!lessonReady) { pendingLens = lens; return; }
  applyingLens = true;
  try {
    if ((engine.params.forces || 'off') !== lens.forces) setForces(lens.forces);
    if (lens.review) review?.start();
  } finally {
    applyingLens = false;
  }
}

/** The host changed something everybody should see. */
function shareLens() {
  if (applyingLens || !lesson?.host || !together) return;
  together.lens({ forces: engine.params.forces || 'off', review: reviewRunning });
}

function onLessonChange(next) {
  const was = lesson;
  lesson = next;
  // A student follows the lens as it is when they arrive.
  if (lesson && !lesson.host && (!was || was.host)) applyLens(lesson.lens);
  // A host coming in sets it from here.
  if (lesson?.host && !was && lessonReady) shareLens();
  paintLesson();
  paintTogether({ connected: together?.connected });
}

function onOffer(offer) {
  offers = [offer, ...offers.filter((o) => o.shared !== offer.shared)].slice(0, 6);
  paintLesson();
  if (!els.togetherHow.open) showRoomSheet();
}

function paintLesson() {
  const host = Boolean(lesson?.host);
  const student = Boolean(lesson && !lesson.host);
  els.togetherTitle.textContent = lesson ? (host ? 'Your lesson' : 'A lesson') : 'Watch this together';
  els.togetherNote.textContent = host
    ? 'You are leading. Everyone who opens this link follows your board, and sees what you switch on.'
    : student
      ? 'The coach is leading. Your board follows theirs; you can look around, and it comes back when they move.'
      : 'Send this link to whoever you want to watch it with. Whatever either of you does to the playhead, both boards do.';
  els.lessonBox.hidden = !room;
  els.lessonStart.hidden = Boolean(lesson);
  els.lessonSay.textContent = host
    ? 'Students join by scanning the code or opening the link. Anything they offer you appears here.'
    : student
      ? (mine?.length ? 'You can offer the coach one of your own games to look at together.' : 'Import your own games in the library, and you can offer one to the coach.')
      : 'Teaching? Make this a lesson: you lead, and everybody else follows your board and what you show on it.';
  els.lessonBig.hidden = !host;
  if (student && mine === null) loadMine();
  els.lessonMineRow.hidden = !(student && mine?.length);
  const roster = lesson?.roster || [];
  els.lessonRoster.hidden = !lesson;
  els.lessonRoster.innerHTML = roster.map((p, i) => {
    const name = p.name || (p.host ? 'The coach' : `Student ${i + 1}`);
    return `<li class="${p.host ? 'host' : ''}">${escapeHtml(name)}${p.host ? ' · leading' : ''}</li>`;
  }).join('');
  els.lessonOffers.hidden = !(host && offers.length);
  els.lessonOffers.innerHTML = offers.map((o, i) => `<li><span><b>${escapeHtml(o.from)}</b> offers ${escapeHtml(o.title)}</span>`
    + `<button class="ctl" type="button" data-offer="${i}">Show it</button></li>`).join('');
  lessonBar.hidden = !lesson;
  if (lesson) {
    const hostName = roster.find((p) => p.host)?.name;
    const following = Math.max(0, roster.length - 1);
    lessonBar.textContent = host
      ? `Leading a lesson · ${following} following`
      : `Lesson${hostName ? ` with ${hostName}` : ''} · following the coach`;
  }
}

const lessonBar = document.createElement('button');
lessonBar.type = 'button';
lessonBar.className = 'lesson-bar';
lessonBar.id = 'lesson-bar';
lessonBar.hidden = true;
lessonBar.addEventListener('click', () => showRoomSheet());
stageBox.appendChild(lessonBar);

els.togetherName.value = savedName();
els.togetherName.addEventListener('change', () => {
  together?.rename(els.togetherName.value.trim());
});
els.lessonStart.addEventListener('click', () => {
  together?.startLesson();
  count('lesson-start', gameId);
});
els.lessonBig.addEventListener('click', () => {
  const link = new URL(location.href);
  link.searchParams.set('with', room);
  link.searchParams.delete('lead');
  els.lessonBigLink.textContent = publicUrl(link);
  drawQr(els.lessonBigQr, publicUrl(link), { scale: 12 });
  els.lessonBigView.showModal();
});
els.lessonOffer.addEventListener('click', () => {
  const g = mine?.[Number(els.lessonMine.value)];
  if (!g) return;
  together?.offer({ game: g.id, shared: g.shared, title: g.label });
  els.lessonSay.textContent = 'Sending it to the coach…';
});
els.lessonOffers.addEventListener('click', (event) => {
  const button = event.target.closest('[data-offer]');
  const offer = button && offers[Number(button.dataset.offer)];
  if (!offer) return;
  const url = new URL('player.html', location.href);
  url.searchParams.set('shared', offer.shared);
  count('lesson-offer', 'shown');
  location.href = takeRoomTo(url.toString());
});

/** The button, the sheet, and who else is in the room. */
/** Who was in the room when the relay last said, for a repaint that does not know. */
let lastPeers = null;

function paintTogether({ connected = null, peers: told = null, why = '' } = {}) {
  if (told) lastPeers = told;
  const peers = told || lastPeers;
  els.together.setAttribute('aria-pressed', room ? 'true' : 'false');
  els.together.title = room ? 'Watching with somebody (w)' : 'Watch this with somebody (w)';
  if (!room) return;
  if (why) { els.togetherPeers.textContent = why; return; }
  if (connected === false) { els.togetherPeers.textContent = 'Connecting...'; return; }
  if (lesson) {
    const n = (lesson.roster || []).length;
    els.togetherPeers.textContent = `${n} ${n === 1 ? 'person' : 'people'} in the lesson.`;
    return;
  }
  // Everyone in the room, this page included. The relay counts seats and watchers
  // separately because the play pages need it to; here they are all just people
  // in front of a board, so they are added up.
  const here = peers ? Number(peers.watchers || 0) + (peers.w ? 1 : 0) + (peers.b ? 1 : 0) : 1;
  els.togetherPeers.textContent = here > 1
    ? `${here} people here — whatever any of you does, all the boards do.`
    : 'Nobody else here yet. The link is live; they will land where you are.';
}

/**
 * Open a room, or reopen the sheet on the one that is already open.
 *
 * `quiet` is for the visitor who ARRIVED on a room link. They did not ask to
 * invite anybody — they were invited — so the room is joined and the sheet is
 * not shown. It is one press away on the button, which is now lit.
 */
function openTogether({ quiet = false } = {}) {
  if (!room) {
    room = roomFromUrl() || newRoomCode();
    markRoomUrl(room);
    together = joinRoom({
      room,
      lead: leading,
      onAt: goWhereTheySay,
      // Nothing to catch up to, so this page is what the room is looking at. The
      // room is empty, or this page has just brought it to another game.
      onReady: reportPlayhead,
      onState: paintTogether,
      onLesson: onLessonChange,
      onLens: applyLens,
      onOffer,
      onOffered: () => { els.lessonSay.textContent = 'Sent. The coach can put it on the board.'; },
    });
    paintTogether({ connected: false });
    count('watch-together', gameId);
  }
  if (!quiet) showRoomSheet();
}

function showRoomSheet() {
  const link = new URL(location.href);
  link.searchParams.set('with', room);
  const text = publicUrl(link);
  els.togetherLink.value = text;
  // `drawQr` returns null for something too long to fit in the versions it draws.
  // No code is better than a code that will not scan.
  els.togetherQrBox.hidden = !drawQr(els.togetherQr, text, { scale: 6 });
  els.togetherShare.hidden = !navigator.share;
  paintTogether({ connected: together?.connected });
  // Looked up again each time: a student may have imported a game since.
  if (lesson && !lesson.host) mine = null;
  paintLesson();
  if (!els.togetherHow.open) els.togetherHow.showModal();
}

function leaveRoom() {
  together?.leave();
  together = null;
  room = null;
  lastPeers = null;
  lesson = null;
  offers = [];
  paintLesson();
  markRoomUrl(null);
  paintTogether();
  els.togetherHow.close();
}

els.together.addEventListener('click', () => openTogether());
els.togetherLeave.addEventListener('click', leaveRoom);
els.togetherCopy.addEventListener('click', async () => {
  try {
    await navigator.clipboard.writeText(els.togetherLink.value);
    els.togetherCopy.textContent = 'Copied';
    setTimeout(() => { els.togetherCopy.textContent = 'Copy'; }, 1600);
  } catch {
    // Clipboard refused - an insecure origin, or a browser that wanted a gesture
    // it did not see. Selecting the text is the fallback everybody knows.
    els.togetherLink.select();
    els.togetherCopy.textContent = 'Press Ctrl+C';
  }
});
els.togetherShare.addEventListener('click', () => {
  navigator.share?.({ title: document.title, url: els.togetherLink.value }).catch(() => {});
});

// A link that arrived with a room in it joins it, quietly. See `openTogether`.
if (roomFromUrl()) openTogether({ quiet: true });

/**
 * The page's own settings, shown in the View tab beside the scene's.
 *
 * SAME SHAPE AS A SCENE PARAMETER, on purpose. `view-panel.js` renders whatever
 * it is handed and `applyParam` routes on the key, so a setting that belongs to
 * the page around the board rather than to the render is one entry here and no
 * new machinery: it is grouped, filtered, remembered on the machine, listed
 * under "You changed" and put back by Reset like everything else. The only field
 * a scene spec does not have is `value`, which is how the panel reads something
 * that is not in `engine.params`.
 *
 * OFF BY DEFAULT. The plaques on the table are the picture's own answer to who is
 * playing (`names`, on by default here), and two copies of a name in one frame is
 * one too many. These are the answer for anyone who wants it in the chrome as
 * well — and for a phone, where the plaques' type is five pixels tall and these
 * are the only legible copy of it.
 */
const PAGE_SPECS = {
  plates: {
    type: 'toggle', label: 'Nameplates in the corners', group: 'Page',
    default: false, live: true, value: () => cornerPlates,
  },
};

// The URL first, then what was stored, then off — the precedence `prefs` sets out.
let cornerPlates = truthy(app.get('plates') ?? prefs.plates);

/**
 * The two nameplates, by the side each belongs to.
 *
 * Their own object rather than six more entries in `els`, because everything
 * below wants them a side at a time: the game hands each plate a name, the
 * playhead hands one of them the move, and the camera decides which end of the
 * board each is standing at. See the section after `dressPage`.
 */
const plates = {
  box: $('plates'),
  w: plateParts('plate-w'),
  b: plateParts('plate-b'),
};

function plateParts(id) {
  const box = $(id);
  return {
    box,
    men: box.querySelector('.men'),
    name: box.querySelector('.name'),
    state: box.querySelector('.state'),
  };
}

function dressPage() {
  const g = meta207();
  const row = INDEX.find((r) => r.id === gameId) || INDEX[0];
  // A PGN header may carry no date at all, and `Game () — move by move` is worse
  // than saying nothing. Every game in the repertoire has one.
  const when = g.date ? ` (${g.date})` : '';
  document.title = `${g.title}${when} — move by move in 3D`;
  document.querySelector('meta[name="description"]')?.setAttribute('content', g.blurb);
  // This page is the app; the reading page is the CONTENT, and it is the one worth
  // indexing — it has the annotation in it as words rather than as a script that
  // has to run first. So every deep link into the player, at whatever move, points
  // search engines at one page per game instead of ten near-identical shells.
  //
  // An imported game has no reading page and never will: those are written out of
  // `games.js` at build time and this one arrived in a file on somebody's desk. So
  // the link goes, and the canonical points at the library rather than at a 404 —
  // `hidden` would be overridden by the stylesheet's own `display`, hence the
  // inline one.
  const own = isImported(g.id);
  const page = own ? '/' : `../games/${g.id}.html`;
  // Created here rather than edited: player.html carries none, for the reason
  // given there. Absolute, because a canonical is read without the page's base.
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.append(canonical);
  }
  canonical.href = new URL(page, location.href).href;
  els.readLink.style.display = own ? 'none' : '';
  els.readLink.href = page;
  els.title.textContent = g.title;
  els.subtitle.textContent = `${g.white} — ${g.black} · ${g.opening}`;
  els.blurb.textContent = g.blurb;
  els.duration.textContent = clock(total / fps);
  els.tabMovesN.textContent = String(plies().length);

  // The facts as pills rather than a line of mono text: each of these is a thing
  // somebody might want to check mid-game, and a paragraph is a bad place to
  // check anything. The result carries who won, since "1–0" is a notation not
  // everybody arriving from a search reads.
  //
  // `pill-set` is named apart from the rest because it is the odd one out: the
  // others are facts about the GAME, and it is the state of a setting — which set
  // this board happens to be wearing. The header strip and the View tab both
  // change it and both show it, so on a phone the stylesheet drops this one and
  // keeps the four that are the game.
  const winner = row.winner ? ` ${row.winner.split(' ').at(-1)}` : '';
  // A game whose finish was published rather than played gets a pill of its own,
  // next to the ply count it qualifies. The player is where somebody scrubs to
  // move 30 of a game that ended at 25, so this is the surface that most needs to
  // say it — and saying it in a pill rather than in the blurb means it is there
  // whether or not the blurb is expanded.
  const codaPill = row.coda
    ? `<span class="pill">last ${row.coda.count} not played</span>` : '';
  // IN WORDS, NOT NOTATION. "1–0 Morphy", "33 half-moves" and "C41" are three
  // facts a player reads at a glance and a newcomer cannot read at all; "Morphy
  // won", "Checkmate in 17" and "Philidor Defence" are the same three facts.
  const decided = g.result === '1–0' || g.result === '0–1';
  const won = decided
    ? `${(row.winner || (g.result === '1–0' ? 'White' : 'Black')).split(' ').at(-1)} won`
    : 'A draw';
  const ended = row.ending === 'mate' ? `Checkmate in ${moveTotal}`
    : row.ending === 'drawn' ? `Drawn after ${moveTotal} moves`
      : row.ending === 'resigned' ? `Resigned after ${moveTotal} moves`
        : `${moveTotal} moves`;
  els.pills.innerHTML = `
    <span class="pill win" title="${escapeHtml(g.result + winner)}">${escapeHtml(won)}</span>
    <span class="pill" title="${plies().length} half-moves">${escapeHtml(ended)}</span>${codaPill}
    <span class="pill" title="${escapeHtml(row.eco || '')}">${escapeHtml(row.family || g.opening || row.eco || '')}</span>
    <span class="pill pill-set"><span class="set" aria-hidden="true"
      ><span style="background:${row.set.light}"></span><span style="background:${row.set.dark}"></span
      ><span style="background:${row.set.dark}"></span><span style="background:${row.set.light}"></span
    ></span>${escapeHtml(row.set.label)}</span>
    <span class="pill" title="${escapeHtml([g.event, g.date].filter(Boolean).join(' · '))}">${escapeHtml([g.event, g.date].filter(Boolean).join(' · '))}</span>`;

  els.blurbToggle.addEventListener('click', () => {
    const open = els.blurbToggle.getAttribute('aria-expanded') === 'true';
    els.blurbToggle.setAttribute('aria-expanded', String(!open));
  });

  for (const rate of SPEEDS) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = `${rate}×`;
    button.setAttribute('aria-pressed', String(rate === speed));
    button.addEventListener('click', () => setSpeed(rate));
    button.dataset.rate = String(rate);
    els.speeds.appendChild(button);
  }
}

// ---------------------------------------------------------------- the players

/**
 * WHO IS PLAYING, AT THE END OF THE BOARD THEY ARE PLAYING FROM.
 *
 * The two names were in the header and nowhere else, which reads as a line of
 * credits: it says who the players are and not which of them is the set of men
 * you are looking at — and that is the question somebody watching actually has,
 * because the board says where White is and nothing says who White is. A plate
 * at each end of the frame answers it where the answer is wanted, and answers
 * "whose move is it" on the way past.
 *
 * CHROME AS WELL AS GEOMETRY, because the scene has the geometry now: two
 * plaques lying on the table at the ends of the board, which is what `names`
 * draws and what this page asks for by default. The argument that used to be
 * here — that type in the scene would be baked into every capture, would want a
 * font loaded first, and would turn with the board — was half wrong and half
 * beside the point, and it is worth saying what the difference actually is.
 *
 * A plaque is IN the room. It faces its own player, so it is legible from one
 * side and edge-on or upside down from the others; it is only readable at all
 * where the board is drawn large; and it has room for a name and nothing else.
 * These are over the canvas: crisp at any size, both names at once, always the
 * right way up, and with the room to say whose move it is and who won. Neither
 * is the better answer, so neither wins by default — see `PAGE_SPECS`, which is
 * the switch between them. What these DO follow is the camera: see `facePlates`.
 *
 * Three things drive them and they move on three different clocks, which is why
 * they are three functions: the names are the game's and are set once, the lit
 * plate is the playhead's and changes with every half-move, and the near end is
 * the camera's and changes with every frame of an orbit.
 */
function dressPlates() {
  const g = meta207();
  plates.w.name.textContent = g.white;
  plates.b.name.textContent = g.black;
  endWord = endWords(BY_ID.get(gameId));
  paintPlates();
  showPlates();
  markPlates(shownPly);
}

/**
 * What the plates say once the game is over.
 *
 * Off the library row's `winner` rather than off the board, because the row has
 * already done this work for the browse page and four of the games are Black
 * wins. "Won" covers a mate and a resignation both, which is as much as the
 * score of a game can tell anybody.
 */
function endWords(row) {
  if (row.winner === row.white) return { w: 'won', b: 'lost' };
  if (row.winner === row.black) return { w: 'lost', b: 'won' };
  // A draw says so on both plates. A game whose result nobody recorded — `*`,
  // which only an imported PGN can carry — says nothing rather than claiming one.
  return row.result === '*' ? { w: '', b: '' } : { w: 'drew', b: 'drew' };
}

let endWord = { w: '', b: '' };

/**
 * Which plate is lit, and what the second line of each says.
 *
 * The side TO MOVE rather than the side that just moved, which is the same
 * choice a clock makes and the useful one: the board is showing a position, and
 * what a reader of a position wants to know is who is about to break it.
 */
function markPlates(k) {
  const list = plies();
  const over = k >= list.length - 1;
  // Before the game starts it is White's, and after any half-move it is the
  // other side's. `k` is negative on the opening card.
  const turn = k < 0 || list[k].side === 'b' ? 'w' : 'b';
  for (const side of ['w', 'b']) {
    const plate = plates[side];
    plate.box.classList.toggle('over', over);
    plate.box.setAttribute('aria-current', String(!over && side === turn));
    plate.state.textContent = over ? endWord[side] : (side === turn ? 'to move' : '');
  }
}

/**
 * The men on each plate, in the colour the men on the board are wearing.
 *
 * Out of `ctx.set` — the five colours the scene resolved for itself — so a set
 * chosen in the header strip is on the plate as well, and a custom one typed
 * into the View panel is too, with no second copy of the palette here to
 * disagree with the board.
 */
function paintPlates() {
  const set = ctx().set;
  plates.w.men.style.background = set.white;
  plates.b.men.style.background = set.black;
}

/**
 * And whether they are there at all, which is a setting and nothing else.
 *
 * IT WAS INFERRED BEFORE THIS, and inferring it was the wrong idea. The rule was
 * "stand down if the picture is already naming the players" — the plaques on the
 * table, or the video's own card and strip — with an exception for a window too
 * narrow to read the plaques in. Every part of that was defensible and the whole
 * of it was a surprise: two names appear or do not appear depending on three
 * other switches and the width of the window, and nothing anywhere says so.
 *
 * One switch instead, off by default, in the View tab next to the plaques'.
 * `.on` rather than the `hidden` attribute because the base rule in the
 * stylesheet is `display: none` — so they are absent before this file has even
 * loaded, which is what "off by default" ought to mean.
 */
function showPlates() {
  plates.box.classList.toggle('on', cornerPlates);
}

/**
 * The near player at the foot of the frame.
 *
 * A plate at the top of a board seen from behind White is Black's, and the same
 * plate is White's the moment the camera goes round the other side — so this
 * follows the camera rather than picking an end and hoping. Read straight off
 * the azimuth, which is the number the scene is placed from: the whole of it is
 * which side of the board the camera is standing on.
 *
 * Called from every draw, so it is written to cost nothing: one number read, one
 * comparison, and the DOM touched only on the frame the ends actually swap.
 */
function facePlates() {
  const side = nearSide(cameraAz());
  if (!side || side === nearest) return;
  nearest = side;
  // `order`, and that is the whole swap — see `.plates` in the stylesheet.
  plates.w.box.style.order = side === 'w' ? '1' : '0';
  plates.b.box.style.order = side === 'b' ? '1' : '0';
}

let nearest = null;

/**
 * Which end of the board the camera is on, or null for edge-on — where neither
 * end is nearer and the honest answer is to leave the plates where they are.
 *
 * The dead band is what makes that answer stable. Crossing 90 degrees IS a swap
 * and the plates should take it, but the `Side` preset parks a degree or two off
 * that line and a drag wanders over it: without the band a plate flicks from one
 * end of the board to the other while the hand is still moving.
 */
function nearSide(az) {
  const a = Math.abs(wrapDeg(az));
  if (a > 84 && a < 96) return null;
  return a < 90 ? 'w' : 'b';
}

/**
 * Where the camera is standing, in the one term this needs.
 *
 * The held view when the page has the camera and the scene's own track when it
 * does not — the same two cases `place` and `takeOver` in `board-camera.js` deal
 * in, read the same way. So the plates follow the composed swing under `Auto` as
 * readily as they follow a drag.
 */
function cameraAz() {
  if (boardCam.held) return boardCam.held.az;
  const track = engine.ctx.camTrack;
  if (!track) return 0;
  return track.az[clamp(rendered, 0, track.n - 1)];
}

/**
 * The move list: every half-move, its lead phrase, and its note where there is
 * one — `games.js`'s `notes`, which is the annotation already written for the
 * video, surfaced as clickable text instead of a card that scrolls past once.
 *
 * Phase titles divide it, out of the same `phases` the camera is composed from,
 * so the list and the scrub bar are cut into the same stretches.
 */
function buildList() {
  const g = meta207();
  const frag = document.createDocumentFragment();
  const rows = [];
  let phaseIx = -1;

  plies().forEach((ply, k) => {
    const at = g.phases.findIndex((phase, i) => (
      k <= phase.through && (i === 0 || k > g.phases[i - 1].through)
    ));
    if (at !== phaseIx) {
      phaseIx = at;
      const head = document.createElement('li');
      head.className = 'phase-head';
      head.textContent = g.phases[at]?.title ?? '';
      frag.appendChild(head);
    }

    const note = g.notes[k] || {};
    const weight = note.weight ?? 1;
    // 1.8 is "given more of the clock than a plain move", which is most of the
    // annotated ones. The star is the other threshold the page already uses —
    // the one the scrub bar draws a BIG dot at — so it lands on the two or three
    // moves a game is actually watched for rather than on every note.
    const heavy = weight >= 1.8;
    const famous = weight >= 2.4;
    const li = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `row ${ply.side}${heavy ? ' heavy' : ''}`;
    // The number on White's ply only, the way a score is written. And a dot on
    // the moves that have something written about them, so where the substance
    // is in a fifty-move game is visible without opening every row.
    button.innerHTML = `
      <span class="no">${ply.side === 'w' ? `${ply.moveNo}.` : ''}</span>
      <span class="san">${escapeHtml(ply.san)}</span>
      <span>
        <span class="lead">${escapeHtml(note.lead || '')}${note.note ? '<span class="dot"></span>' : ''}</span>
        ${note.note ? `<span class="note">${escapeHtml(note.note)}</span>` : ''}
        ${famous ? '<span class="star"><svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor"'
          + ' aria-hidden="true"><path d="M6 0.5l1.6 3.5 3.9.4-2.9 2.6.8 3.8L6 8.9l-3.4 1.9.8-3.8L.5 4.4l3.9-.4z"></path>'
          + '</svg>the move it is famous for</span>' : ''}
      </span>`;
    button.addEventListener('click', () => { userScrolled = 0; goToPly(k); });
    li.appendChild(button);
    frag.appendChild(li);
    rows.push(button);
  });

  els.list.appendChild(frag);
  return rows;
}

/**
 * The scrub bar's landmarks, all of them already in the data.
 *
 * The phase spans come from the timeline, so the openings, the queen hunt and the
 * mating net are visible as stretches before anything is played. The dots are the
 * moves carrying a weight — the ones the game is watched for, which are also
 * exactly the moves given more of the clock, so a wide segment and a dot are two
 * views of one number.
 */
function buildTrack() {
  const t = tl();
  const seconds = total / fps;
  const pct = (x) => `${(100 * x / seconds).toFixed(3)}%`;

  // Called again after a rebuild, because `emphasis` moves every move's start
  // time and so every landmark on this bar.
  els.rail.innerHTML = '';
  for (const old of els.track.querySelectorAll('.mark')) old.remove();

  const spans = [
    { from: 0, to: t.intro, kind: 'bookend' },
    ...t.phaseSpan.map((phase) => ({ from: phase.t0, to: phase.t1, kind: 'play' })),
    { from: t.gameEnd, to: seconds, kind: 'bookend' },
  ];
  for (const span of spans) {
    const div = document.createElement('div');
    div.className = `phase ${span.kind}`;
    div.style.left = pct(span.from);
    div.style.width = pct(Math.max(0, span.to - span.from));
    els.rail.appendChild(div);
  }

  const notes = meta207().notes;
  plies().forEach((ply, k) => {
    const weight = notes[k]?.weight ?? 1;
    if (weight < 1.8) return;
    const dot = document.createElement('div');
    dot.className = weight >= 2.4 ? 'mark big' : 'mark';
    dot.style.left = pct(t.start[k]);
    dot.title = `${ply.moveNo}${ply.side === 'w' ? '.' : '…'}${ply.san} — ${notes[k]?.lead ?? ''}`;
    els.track.appendChild(dot);
  });

  els.track.setAttribute('aria-valuemax', String(lastFrame));

  // The phase names, over the stretch each one names. Same percentages as the
  // segments above, so they cannot drift apart — and the strip sits in the
  // scrub row's grid, so it is exactly as wide as the track whatever the clocks
  // either side of it come out to.
  els.phaseStrip.innerHTML = '';
  t.phaseSpan.forEach((phase, i) => {
    const label = document.createElement('span');
    label.style.left = pct(phase.t0);
    label.style.width = pct(Math.max(0, phase.t1 - phase.t0));
    label.textContent = phase.title;
    label.title = phase.title;
    label.dataset.phase = String(i);
    els.phaseStrip.appendChild(label);
  });

  // And the same stretches as somewhere to jump to, in the panel.
  els.phaseJump.innerHTML = '';
  t.phaseSpan.forEach((phase, i) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.innerHTML = `<span class="ph-n" aria-hidden="true">${i + 1}</span>`
      + `<span class="ph-t">${escapeHtml(phase.title)}</span>`;
    button.title = `${phase.title} — from move ${plies()[phase.from].moveNo}`;
    button.dataset.phase = String(i);
    button.addEventListener('click', () => { userScrolled = 0; goToPly(phase.from, { animate: false }); });
    els.phaseJump.appendChild(button);
  });
}

// ------------------------------------------------------------------ forces

/**
 * FORCES — which side controls each square (`paintForces` in the scene).
 *
 * A button beside 2D/3D, because it is a way of looking at the board and belongs
 * with the other ways of looking at it; `c` cycles all four settings, and the
 * View tab has them as a list. The legend says what the colours mean while it
 * is on, since a board washed in blue and red explains nothing by itself.
 */
const FORCE_ORDER = ['off', 'both', 'w', 'b'];
// Said as what it shows rather than what it is called: "Forces" is a term, and
// the button is read by people who have never met it.
const FORCE_WORDS = {
  off: 'Who controls each square',
  both: 'Who controls each square',
  w: 'White’s squares only',
  b: 'Black’s squares only',
};
let forcesButton = null;
let forcesLegend = null;

function mountForces() {
  const row = els.views.parentNode;
  if (!row?.classList.contains('canvas-tools') || !engine.specs.forces) return;
  const box = document.createElement('div');
  box.className = 'dims forces-tools';
  box.setAttribute('role', 'group');
  box.setAttribute('aria-label', 'Forces on the board');
  forcesButton = document.createElement('button');
  forcesButton.type = 'button';
  forcesButton.id = 'forces';
  forcesButton.title = 'Forces: which side controls each square (c)';
  forcesButton.addEventListener('click', () => setForces(engine.params.forces === 'off' ? 'both' : 'off'));
  // The words on a wide page, the icon on a phone — see `.forces-tools` in watch.css.
  forcesButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"'
    + ' stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><rect x="2.5" y="2.5" width="11" height="11" rx="1.2"></rect>'
    + '<path d="M3.2 12.8 12.8 3.2v9.6z" fill="currentColor" stroke="none" opacity=".5"></path></svg>'
    + '<span class="forces-word"></span>';
  box.appendChild(forcesButton);
  row.insertBefore(box, row.firstChild);
  // Up to the masthead as an icon, after 2D / 3D, at every width: off the picture.
  const slot = document.getElementById('install-slot');
  if (slot) slot.before(box);
  forcesLegend = document.createElement('div');
  forcesLegend.className = 'forces-legend';
  forcesLegend.innerHTML = '<span class="fl-w"><span>White<span class="fl-long"> controls</span></span></span>'
    + '<span class="fl-b"><span>Black<span class="fl-long"> controls</span></span></span>'
    + '<span class="fl-c">both sides</span><span class="fl-h">outnumbered</span>';
  stageBox.appendChild(forcesLegend);
  paintForcesButton();
}

function paintForcesButton() {
  if (!forcesButton) return;
  const value = engine.params.forces || 'off';
  forcesButton.querySelector('.forces-word').textContent = FORCE_WORDS[value] || 'Forces';
  forcesButton.setAttribute('aria-pressed', String(value !== 'off'));
  forcesLegend.hidden = value === 'off';
  placeForcesLegend();
}

/**
 * Under the board, at the top of the playback bar, in 2D and 3D alike: over the
 * picture it covered the far ranks, and on a phone it wrapped across the back rows.
 * The stage gives up a line of height for it and `stage-size.js` refits.
 */
function placeForcesLegend() {
  if (!forcesLegend) return;
  forcesLegend.classList.add('below');
  const tools = document.getElementById('board-tools');
  if (forcesLegend.previousElementSibling !== tools) tools.after(forcesLegend);
}

function setForces(value) {
  if (!engine.specs.forces) return;
  applyParam('forces', engine.specs.forces, value);
  viewPanel?.refresh();
  paintForcesButton();
  shareLens();
}

function cycleForces() {
  const at = FORCE_ORDER.indexOf(engine.params.forces || 'off');
  setForces(FORCE_ORDER[(at + 1) % FORCE_ORDER.length]);
}

// ------------------------------------------------------------------ the review

/** The review, once mounted. See `review-ui.js`. Null in `?video=1`, which renders the upload. */
let review = null;
/** The move list's buttons, one per half-move. Rebuilt when a game gets chapters. */
let rows = [];
/** Whether this page has already given the game its chapters. */
let chaptered = false;

/**
 * A visitor's game, once reviewed, gets chapters (`chaptersOf`): kept with the
 * game, and applied now by rebuilding the scene, which is what re-divides the
 * timeline, the phase strip and the camera. Once per page, and only when they
 * differ from what the game already has — a game reviewed on an earlier visit
 * opens with them and needs nothing.
 */
async function giveChapters(result) {
  if (chaptered || !isImported(gameId)) return;
  const chapters = chaptersOf(result, plies().map((p) => p.san));
  if (!chapters) return;
  chaptered = true;
  const now = meta207().phases.map((p) => `${p.title}|${p.through}`).join(',');
  if (chapters.map((p) => `${p.title}|${p.through}`).join(',') === now) return;
  if (!(await setChapters(gameId, chapters))) return;
  const held = holding();
  engine.setParams({ game: gameId });
  els.list.innerHTML = '';
  rows = buildList();
  afterRebuild(held);
}

/**
 * The line under the board for a move the annotation says nothing about: what the
 * review made of it. A famous game's own lead always wins — a person wrote it.
 */
function reviewLead(k) {
  const r = review?.result();
  const m = r?.moves[k];
  if (m?.only) return `The only good move here — found${m.hard ? ` · ${m.hard.word}` : ''}`;
  if (!m?.level) return m?.think ? 'A moment to stop and think' : '';
  const word = r.turning?.k === k ? `Turning point · ${m.word}` : m.word;
  return m.best ? `${word} — ${m.best} was better` : word;
}

function mountPageReview() {
  if (videoMode) return null;
  return mountReview({
    sanMoves: plies().map((p) => p.san),
    imported: isImported(gameId),
    els: {
      box: $('review'), go: $('review-go'), status: $('review-status'), sum: $('review-sum'),
      graph: $('review-graph'), bar: els.track.closest('.bar'),
    },
    rows: () => rows,
    seconds: () => total / fps,
    startOf: (k) => tl().start[k],
    endAt: () => tl().gameEnd,
    onReview: (result) => {
      if (shownPly >= 0) els.atLead.textContent = meta207().notes[shownPly]?.lead || reviewLead(shownPly);
      giveChapters(result);
    },
    // Which kind of game, never which game: `watch-game` already says that.
    onStart: () => {
      count('review-open', isImported(gameId) ? 'imported' : 'famous');
      // In a lesson the coach's review is everybody's.
      reviewRunning = true;
      shareLens();
    },
    label: (k) => `${plies()[k].moveNo}${plies()[k].side === 'w' ? '.' : '…'}${plies()[k].san}`,
    goTo: (k) => { userScrolled = 0; goToPly(k); },
    playOnUrl: (ply, side) => playOnUrl({ game: gameId, ply, as: side }),
    onShare: (turning) => shareMoment(turning),
  });
}

/**
 * The way from somebody else's turning point to your own.
 *
 * A shared moment (`momentLink` in `share-card.js`, tagged `utm_source=moment`)
 * is the one arrival that has just been shown what the review does, on a game
 * that is not theirs. The share is only a loop if it hands them the same thing
 * for their own games, so they get the import straight away: the library opens
 * its dialog on `#import`. Read from `app`, which was parsed before `syncUrl`
 * took the tags out of the address bar.
 */
function mountMomentInvite() {
  const box = $('moment-invite');
  if (!box || videoMode || document.documentElement.dataset.embed === '1') return;
  if (app.get('utm_source') !== 'moment') return;
  box.hidden = false;
  count('moment-invite', 'shown');
  $('moment-invite-go').addEventListener('click', () => count('moment-invite', 'click'));
}

/**
 * The turning point as a card and a link (`share-card.js`), loaded when asked
 * for: most visitors never share anything, and the card code need not load for
 * them.
 */
async function shareMoment(turning) {
  const { drawMomentCard, momentLink, momentOf, openShareSheet } = await import('./share-card.js');
  const g = meta207();
  const result = review?.result();
  if (!result || result.turning?.k !== turning.k) return;
  const sanMoves = plies().map((p) => p.san);
  // Whose game it is, when it was fetched by username: "Lichess · name".
  const me = isImported(gameId) ? String(g.source || '').split(' · ')[1] || null : null;
  const moment = momentOf({ game: g, result, sanMoves, me });
  if (!moment) return;
  const canvas = drawMomentCard(document.createElement('canvas'), moment, {
    sanMoves, chances: result.chances, sharp: result.sharp,
  });
  const link = publicUrl(momentLink({
    origin: location.origin, base: location.pathname, gameId, imported: isImported(gameId),
    game: g, sanMoves, k: moment.k,
  }));
  openShareSheet({
    canvas, link, title: `${moment.kicker}: ${moment.label}`,
    // How it went out, never where to or what was in it.
    onShared: (how) => count('share-card', how),
  });
}

/** Which phase a half-move falls in, or -1 before the game starts. */
function phaseOf(k) {
  if (k < 0) return -1;
  return tl().phaseSpan.findIndex((phase) => k >= phase.from && k <= phase.to);
}

// ------------------------------------------------------------- the story card

const STORY_KEY = 'immortal-story';
const story = {
  box: $('story'), chapter: $('story-chapter'), san: $('story-san'), lead: $('story-lead'),
  next: $('story-next'), why: $('story-why'), whyBtn: $('story-why-btn'),
  hide: $('story-hide'), show: $('story-show'),
};
const moveName = (p) => `${p.moveNo}${p.side === 'w' ? '.' : '…'} ${p.san}`;

/**
 * The card over the board: which chapter this is, the move, what it was for,
 * and what comes next. Every word is the move list's own — `notes[k].lead`,
 * `notes[k].note` behind "Why this move?", the phase titles — so the card and
 * the list cannot disagree, and a game with no notes simply says less.
 */
function tellStory(k) {
  const g = meta207();
  const list = plies();
  const spans = tl().phaseSpan;
  const ph = phaseOf(k);
  story.chapter.textContent = k < 0 ? 'Before the first move'
    : spans[ph] ? `Chapter ${ph + 1} of ${spans.length} · ${spans[ph].title}` : '';
  story.san.hidden = k < 0;
  if (k >= 0) story.san.textContent = moveName(list[k]);
  story.lead.textContent = k < 0 ? (g.opening || g.title) : (g.notes[k]?.lead || reviewLead(k) || '');
  const n = list[k + 1];
  const nextLead = g.notes[k + 1]?.lead;
  story.next.textContent = n ? `Next: ${moveName(n)}${nextLead ? ` — ${nextLead}` : ''}`
    : 'The end of the game.';
  const why = k >= 0 ? g.notes[k]?.note || '' : '';
  story.why.textContent = why;
  story.whyBtn.hidden = !why;
  story.why.hidden = !why || story.whyBtn.getAttribute('aria-expanded') !== 'true';
}

function mountStory() {
  let off = false;
  try { off = localStorage.getItem(STORY_KEY) === 'off'; } catch { /* private */ }
  const set = (hide) => {
    story.box.hidden = hide;
    story.show.hidden = !hide;
    try { if (hide) localStorage.setItem(STORY_KEY, 'off'); else localStorage.removeItem(STORY_KEY); } catch { /* private */ }
  };
  story.hide.addEventListener('click', () => { set(true); story.show.focus(); });
  story.show.addEventListener('click', () => { set(false); story.hide.focus(); });
  story.whyBtn.addEventListener('click', () => {
    const open = story.whyBtn.getAttribute('aria-expanded') !== 'true';
    story.whyBtn.setAttribute('aria-expanded', String(open));
    story.whyBtn.textContent = open ? 'Less' : 'Why this move?';
    story.why.hidden = !open || !story.why.textContent;
  });
  set(off);

  // WHERE IT GOES. Over the board wherever the board has room round it; at the
  // head of the panel on a phone or a tablet held upright, where the board is
  // docked to the top of the screen and a card over it would sit on the pieces.
  // The query is the stylesheet's own one-column rule, word for word.
  // And in 2D wherever it is: a flat board fills the frame edge to edge, so
  // there is no table round it for a card to stand on either.
  const oneColumn = matchMedia('(max-width: 900px), (orientation: portrait) and (max-width: 1100px)');
  const place = () => {
    const flat = canvas.classList.contains('flat');
    const home = oneColumn.matches || flat ? document.querySelector('.panel') : stageBox;
    // Over the board, the card floats there but its Show button waits under it.
    const showHome = home === stageBox ? document.getElementById('board-tools') : home;
    if (story.box.parentNode === home && story.show.parentNode === showHome) return;
    home.prepend(story.box);
    showHome.prepend(story.show);
  };
  oneColumn.addEventListener('change', place);
  new MutationObserver(place).observe(canvas, { attributes: true, attributeFilter: ['class'] });
  place();
  tellStory(shownPly);
}

// ------------------------------------------------------------ the camera menu

/**
 * The six angles as one button over the board. The menu itself is shared with
 * every board page with the friendly look — see `../camera-menu.js`; this page's
 * `VIEWS` already carries friendly names, so none are passed.
 */
function mountCameraMenu() {
  mountSharedCameraMenu(els.views);
}

/**
 * THE SWITCHES UNDER THE BOARD. The camera's angle was a button floating over
 * the top of the picture, on the far ranks; on anything wider than a phone it
 * goes to `#board-tools` at the top of the bar, with Show the story. (Who
 * controls each square is an icon in the masthead — `mountForces`.) On a phone
 * the row stays where `board-camera.js` put it. Same elements, moved: handlers,
 * ids and lit state are untouched. After `mountLeanLayout`, which looks for the
 * 2D / 3D switch inside the stage.
 */
function mountBoardTools() {
  const tools = document.getElementById('board-tools');
  const row = els.views.parentNode;
  if (!tools || !row?.classList.contains('canvas-tools')) return;
  const phone = matchMedia('(max-width: 560px)');
  const place = () => {
    if (phone.matches) { if (row.parentNode !== stageBox) stageBox.appendChild(row); }
    else if (row.parentNode !== tools) tools.appendChild(row);
    applyStageSize(true);
  };
  place();
  phone.addEventListener('change', place);
}

/**
 * "MAKE IT YOURS": THE FIVE THINGS PEOPLE CHANGE, AS PICTURES.
 *
 * The tab was the View panel and nothing else — forty-odd parameters in groups,
 * which is right for somebody tuning a render and a wall of sliders for everybody
 * else. So the tab now opens on the five that are the LOOK of the game: the board
 * as swatches, the men, the finish, 2D or 3D and the angle, and the overlay of
 * who controls which square. Every one goes through the road the panel's own
 * control takes (`pickQuick`, `setForces`, the camera's `setView`), so the two
 * can never disagree, and the panel follows underneath as "Every setting".
 */
const BOARDS_SHOWN = 8;
const MEN_GLYPH = { club: '\u265C\uFE0E', figures: '\u265E\uFE0E', staunton: '\u265B\uFE0E' };
const FORCE_CHOICES = [['off', 'Off'], ['both', 'Both sides'], ['w', 'White'], ['b', 'Black']];

function buildLook() {
  const specs = engine.specs;
  const look = document.createElement('div');
  look.className = 'look';
  look.id = 'look';
  const section = (title, why) => {
    const box = document.createElement('section');
    box.className = 'look-sec';
    box.innerHTML = `<h3>${escapeHtml(title)}</h3>${why ? `<p>${escapeHtml(why)}</p>` : ''}`;
    look.appendChild(box);
    return box;
  };
  const pill = (label, pressed, onClick) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', onClick);
    quickMarks.push(() => button.setAttribute('aria-pressed', String(pressed())));
    return button;
  };
  const group = (cls, label) => {
    const el = document.createElement('div');
    el.className = cls;
    el.setAttribute('role', 'group');
    el.setAttribute('aria-label', label);
    return el;
  };

  // The board, as the colours it is.
  if (specs.board) {
    const byId = new Map(BOARDS.map((set) => [set.id, set]));
    const colours = (value) => byId.get(value) || ctx().set;
    const box = section('Board');
    const grid = group('look-boards', 'Board');
    specs.board.options.forEach((option, i) => {
      const value = option.value ?? option;
      const name = setName(option.label ?? option);
      const set = colours(value);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'look-board';
      if (i >= BOARDS_SHOWN) button.dataset.more = '';
      button.innerHTML = `<span class="look-tile" style="--l:${set.light};--d:${set.dark}"></span>`
        + `<span class="look-name">${escapeHtml(name)}</span>`;
      button.addEventListener('click', () => pickQuick('board', specs.board, value));
      quickMarks.push(() => button.setAttribute('aria-pressed', String(engine.params.board === value)));
      grid.appendChild(button);
    });
    box.appendChild(grid);
    const rest = specs.board.options.length - BOARDS_SHOWN;
    if (rest > 0) {
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'look-more';
      more.setAttribute('aria-expanded', 'false');
      more.textContent = `Show all ${specs.board.options.length} boards`;
      more.addEventListener('click', () => {
        const open = more.getAttribute('aria-expanded') !== 'true';
        more.setAttribute('aria-expanded', String(open));
        grid.classList.toggle('all', open);
        more.textContent = open ? 'Show fewer' : `Show all ${specs.board.options.length} boards`;
      });
      box.appendChild(more);
    }
  }

  // The men.
  if (specs.men) {
    const box = section('Pieces');
    const row = group('look-cards', 'Pieces');
    for (const option of specs.men.options) {
      const value = option.value ?? option;
      const button = pill('', () => engine.params.men === value, () => pickQuick('men', specs.men, value));
      button.title = option.label ?? value;
      button.innerHTML = `<span class="look-glyph" aria-hidden="true">${MEN_GLYPH[value] || ''}</span>`
        + `<span>${escapeHtml(STYLE_WORD[value] || option.label || value)}</span>`;
      row.appendChild(button);
    }
    box.appendChild(row);
  }

  // What they are made of.
  if (specs.finish) {
    const box = section('Finish');
    const seg = group('look-seg', 'Finish');
    for (const option of specs.finish.options) {
      const value = option.value ?? option;
      seg.appendChild(pill(FINISH_WORD[value] || option.label || value,
        () => engine.params.finish === value, () => pickQuick('finish', specs.finish, value)));
    }
    box.appendChild(seg);
  }

  // Where it is seen from.
  {
    const box = section('Camera');
    if (specs.dimension) {
      const seg = group('look-seg', 'Board in 2D or 3D');
      for (const [value, label] of [['3d', '3D'], ['2d', 'Flat, from above (2D)']]) {
        seg.appendChild(pill(label, () => (engine.params.dimension || '3d') === value,
          () => { pickQuick('dimension', specs.dimension, value); placeForcesLegend(); }));
      }
      box.appendChild(seg);
    }
    const chips = group('look-chips', 'Camera angle');
    for (const [name, preset] of Object.entries(VIEWS)) {
      const button = pill(preset.label, () => boardCam.viewName === name, () => boardCam.setView(name));
      button.dataset.view = name;
      chips.appendChild(button);
    }
    box.appendChild(chips);
    // The angle moves on its own too (a drag, a key, the menu over the board),
    // and none of those go through `applyParam`, so this listens for itself.
    new MutationObserver(markQuick).observe(els.views, { attributes: true, subtree: true });
  }

  // Who controls which square.
  if (specs.forces) {
    const box = section('Who controls each square',
      'Colours every square by the side that attacks it — blue for White, red for Black.');
    const seg = group('look-seg', 'Who controls each square');
    for (const [value, label] of FORCE_CHOICES) {
      seg.appendChild(pill(label, () => (engine.params.forces || 'off') === value, () => { setForces(value); markQuick(); }));
    }
    box.appendChild(seg);
  }

  const every = document.createElement('div');
  every.className = 'look-every';
  every.innerHTML = '<h3>Every setting</h3><p>The whole render, for anybody who wants to tune it.</p>';

  els.view.prepend(look);
  els.view.querySelector('.view-mode')?.after(every);
  markQuick();
}

/**
 * A button that opens the panel's look tab, wherever one is put. There is none
 * in the masthead any more — the tab itself is the way in — so this is a no-op
 * until something on the page asks for it again.
 */
function mountLook() {
  $('look-open')?.addEventListener('click', () => {
    els.tabView.click();
    els.tabView.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    els.tabView.focus({ preventScroll: true });
  });
}

// -------------------------------------------------------------- time and moves

function clock(seconds) {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Which half-move is on the board at a frame.
 *
 * The same three cases as `viewAt` in scene.js, in the same order and off the same
 * timeline: before the game starts it is the opening card, after the last move it
 * is the hold on the mate, and in between it is the last move to have started.
 * The check script pins the two together — a list that highlights a different move
 * than the card in the picture is the exact failure this arrangement is exposed
 * to, so it is tested rather than trusted.
 */
function plyAt(frame) {
  const t = frame / fps;
  const line = tl();
  const list = plies();
  if (t < line.intro) return -1;
  if (t >= line.gameEnd) return list.length - 1;
  let k = list.length - 1;
  while (k > 0 && line.start[k] > t) k--;
  return k;
}

/**
 * The first frame of a half-move.
 *
 * Ceiling rather than round, so `plyAt(frameOfPly(k)) === k` exactly: rounding
 * down lands a hundredth of a second before the move starts, where the card still
 * says the move before and the piece has not left its square.
 */
function frameOfPly(k) {
  return clamp(Math.ceil(tl().start[k] * fps), 0, lastFrame);
}

function moveLabel(k) {
  const ply = plies()[k];
  return `${ply.moveNo}${ply.side === 'b' ? 'b' : ''}`;
}

/**
 * A move out of the URL. `?move=22` is White's 22nd, `?move=22b` (or `22...`) is
 * Black's reply, and `?ply=42` is the half-move index the data itself is keyed by.
 *
 * A number that names no move is ignored rather than clamped: `move=99` on a
 * 45-half-move game is a stale link, and opening at the start is a better answer
 * than opening on the mate.
 */
function plyFromUrl() {
  const exact = app.get('ply');
  if (exact !== null && /^\d+$/.test(exact.trim())) {
    const k = Number(exact.trim());
    return k <= plies().length - 1 ? k : null;
  }
  const raw = (app.get('move') || '').trim().toLowerCase();
  const hit = raw.match(/^(\d+)(b|w|\.{3}|\.)?$/);
  if (!hit) return null;
  const side = hit[2] === 'b' || hit[2] === '...' ? 'b' : 'w';
  const list = plies();
  const exactHit = list.findIndex((p) => p.moveNo === Number(hit[1]) && p.side === side);
  if (exactHit >= 0) return exactHit;
  // A move number that exists but not on that side — Black's 41st in a game that
  // ended on White's — is the move the visitor meant.
  const loose = list.findIndex((p) => p.moveNo === Number(hit[1]));
  return loose >= 0 ? loose : null;
}

/**
 * Keep the angle in the address bar.
 *
 * A preset by name, a dragged view as the three numbers, and nothing at all when
 * it is the default — so `?view=top` and `?view=-35.2,52,1.08` both reproduce, and
 * a link to the default stays clean. `?view=auto` is the video's own camera.
 */
function syncViewUrl() {
  const query = new URLSearchParams(location.search);
  const name = boardCam.viewName;
  const held = boardCam.held;
  if (name === 'angled') query.delete('view');
  else if (name === 'free' && held) {
    // Wrapped here as well as when the glide settles, because the URL can be
    // written mid-drag — a bearing that has been round the board twice renders
    // identically and reads as nonsense in a link.
    const at = [wrapDeg(held.az), held.el, held.zoom];
    // The pan only when there is one, so a link to a view nobody slid is the
    // three numbers it always was and old links go on meaning what they meant.
    if (held.panR || held.panU) at.push(held.panR, held.panU);
    query.set('view', at.map((n) => Math.round(n * 100) / 100).join(','));
  } else query.set('view', name);
  history.replaceState(null, '', `${location.pathname}?${query}`);
}

/**
 * Where you are in this game, for the homepage's "pick up where you left off"
 * — see `chess-app/resume.js`.
 *
 * Only on a move change (`reflect` returns before this otherwise), so a game
 * playing at full speed writes a few hundred bytes a few times a second, and a
 * paused one writes nothing. Not at all in `?video=1`, which is a render, or in
 * an embed, which is somebody else's page showing one of ours: neither is a
 * person who will come back to the homepage wondering where they were.
 *
 * The start is not "in the middle" of anything, so it writes nothing and leaves
 * any earlier place alone. The last move is the end of the game: watched to the
 * end is finished, and the card goes.
 *
 * The test is inside the function and not a `const` beside it because
 * `reflect` runs during boot, and a `const` this far down the file would be a
 * temporal dead zone on the first draw — see the note at `VOICE_KEY`.
 */
function rememberWhere(k) {
  if (videoMode || document.documentElement.dataset.embed === '1' || k < 0) return;
  const of = plies().length;
  if (k >= of - 1) { drop('watch'); return; }
  note('watch', { game: gameId, ply: k, of, title: meta207().title });
}

/** Keep the address bar on the current move, so it is always the link to it. */
function syncUrl(k) {
  const q = new URLSearchParams(location.search);
  q.delete('ply');
  // A share card's tags have been counted by now; left in, a reload or a copied
  // address would be counted as another arrival from the card.
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign']) q.delete(key);
  q.set('game', gameId);
  if (k < 0) q.delete('move');
  else q.set('move', moveLabel(k));
  history.replaceState(null, '', `${location.pathname}?${q}`);
}

// ---------------------------------------------------------------- the playhead

let frame = 0;        // float while playing, so speed is not quantised to a frame
let rendered = -1;    // what is actually on the canvas
let playing = false;
let speed = SPEEDS.includes(Number(app.get('speed'))) ? Number(app.get('speed')) : 1;
let shownPly = -2;
let raf = 0;
let lastTick = 0;
/**
 * A move being played out on its own, without starting the game.
 *
 * The thing this fixes: a step used to be a seek to the FIRST frame of a move —
 * the position before it — so clicking through a game showed a series of stills
 * and every piece appeared to teleport. The travel was there all along (measured:
 * 29 frames of eased motion for 18.Bd6, accelerating and then arriving slow), it
 * simply never got to run, because nothing was advancing the clock.
 *
 * So a step now runs the clock across exactly that move and stops. `{ end }` is
 * the frame to stop on; null means nothing is being played out.
 */
let beat = null;

/**
 * How far the fire on a checked king has been turned past the frame on screen.
 *
 * A step is not a play: it runs the move and STOPS, and what it stops on is one
 * frame held indefinitely. `drawKingHeat` in `scene.js` glows a checked king and
 * burns a mated one, and both of those are things that move — held still, the
 * glow stops reading as light and reads as a king somebody has painted pink,
 * which is exactly what stepping onto a check looked like.
 *
 * So a paused page parked on a check goes on turning `ctx.heat.phase`. The
 * playhead does not move: the position, the camera, the card and the score strip
 * are all still on the frame that was asked for, and only the fire runs. See
 * `phase` in `buildKingHeat` for why it is written for one render and put back,
 * and `glowed` below for the one place that costs something.
 */
let glowAt = 0;
/**
 * Whether the last thing drawn was a turn of the fire rather than the frame
 * itself.
 *
 * `draw` skips a frame it has already drawn, and after a glow the canvas holds
 * frame N with the fire somewhere past it — which is not frame N. Without this,
 * asking for frame N again would be answered with that, and the one caller who
 * must never be answered with that is `scripts/check-player.mjs`, which seeks the
 * player to a frame and compares it against the video's own render of it.
 */
let glowed = false;

/** Is there a fire on the board that a paused page should keep turning? */
function glowing() {
  return !playing && !beat && Boolean(engine.ctx.heat?.lit);
}

function draw(force = false) {
  const target = clamp(Math.round(frame), 0, lastFrame);
  // The camera moving is a reason to draw, the same as the playhead moving: a
  // preset gliding to overhead happens on a board that is standing still.
  const moving = boardCam.step();
  if (!force && target === rendered && !moving && !glowed) return;
  // Before the render, not after it: `placeCamera` reads the track from inside
  // `renderFrame`, so writing the frame's entry first is all it takes — and the
  // frame is then drawn once. See the header.
  const restore = boardCam.place(target);
  engine.renderFrame(target);
  if (restore) restore();
  rendered = target;
  glowed = false;
  drawn++;
  reflect(target);
  // The plates stand at the two ends of the board, so they go round it with the
  // camera. After the render rather than before it: it reads the frame that was
  // just drawn, and draws nothing itself.
  facePlates();
}

/**
 * The same frame again, with the fire a little further on.
 *
 * Written, drawn, and put straight back — the bargain `placeView` strikes with
 * the camera track, for the same reason. Between two of these the scene is
 * exactly as the video left it, so nothing that asks for a frame by index gets
 * this page's idling instead of the video's frame.
 */
function drawGlow() {
  const heat = engine.ctx.heat;
  heat.phase = glowAt;
  draw(true);
  heat.phase = 0;
  glowed = true;
}

/** Everything in the page that follows the playhead. */
function reflect(target) {
  const at = (100 * target / lastFrame).toFixed(3) + '%';
  els.fill.style.width = at;
  els.knob.style.left = at;
  els.clock.textContent = clock(target / fps);
  els.track.setAttribute('aria-valuenow', String(target));

  const k = plyAt(target);
  if (k === shownPly) return;
  shownPly = k;
  markPlates(k);
  rememberWhere(k);

  rows.forEach((row, i) => {
    row.classList.toggle('current', i === k);
    row.classList.toggle('played', i < k);
  });

  const phase = phaseOf(k);
  for (const label of els.phaseStrip.children) {
    label.classList.toggle('on', Number(label.dataset.phase) === phase);
  }
  for (const button of els.phaseJump.children) {
    button.setAttribute('aria-current', String(Number(button.dataset.phase) === phase));
  }

  if (k >= 0) {
    const ply = plies()[k];
    const last = k === plies().length - 1;
    els.track.setAttribute('aria-valuetext', `move ${ply.moveNo}${ply.side === 'w' ? '.' : '…'} ${ply.san}`);
    // The clock is the video's unit; this is the reader's. `of` counts whole
    // moves, which is what a score is numbered in.
    els.atMove.hidden = false;
    els.atMove.className = `at-move ${ply.side}${last ? ' mate' : ''}`;
    els.atMove.innerHTML = `<span class="san">${ply.moveNo}${ply.side === 'w' ? '.' : '&hellip;'}`
      + `${escapeHtml(ply.san)}</span><span class="of">${last ? 'the end' : `move ${ply.moveNo} of ${moveTotal}`}</span>`;
    els.atLead.textContent = meta207().notes[k]?.lead || reviewLead(k);
    markPlayOn(k);
    follow(k);
  } else {
    els.track.setAttribute('aria-valuetext', 'the opening card');
    els.atMove.hidden = true;
    els.atLead.textContent = meta207().opening;
    markPlayOn(k);
  }
  // After the panel, not before: the voice says what the page has just been made
  // to say, which is `voice.js`'s founding rule — there is no second script for
  // the ear that could drift from the one on the screen.
  narrate(k);
  tellStory(k);
  // And the room hears the same thing, for the same reason: `reflect` is the one
  // function every step, scrub, jump and click on the move list goes through, so
  // reporting from here cannot miss one of them.
  reportPlayhead();
  markEnds();
  syncUrl(k);
}

/**
 * Where "play on from here" would take you, from where the playhead is.
 *
 * WHY THE LINK MOVES WITH THE PLAYHEAD rather than being a button that reads the
 * position when it is pressed. Because it is an anchor, and an anchor that says
 * where it goes is one a person can middle-click, copy, or open in a second tab
 * to keep the game they were watching. A button would have been fewer lines and
 * would have taken all three of those away.
 *
 * `k` is the index of the half-move now on the board, so `k + 1` half-moves have
 * been played and the side to move is whoever is on turn in the position being
 * looked at. That is the side offered — the question somebody is asking while
 * they watch is "what would I do HERE", and the answer is only interesting from
 * the chair that has to move.
 *
 * Hidden on the opening card, where the position is the starting one and "play on
 * from here" is an elaborate way of saying "play a game".
 */
function markPlayOn(k) {
  if (!els.playOn) return;
  if (k < 0) { els.playOn.hidden = true; return; }
  const ply = k + 1;
  els.playOn.hidden = false;
  els.playOn.href = playOnUrl({ game: gameId, ply, as: ply % 2 === 0 ? 'w' : 'b' });
  const side = ply % 2 === 0 ? 'White' : 'Black';
  els.playOn.textContent = `Play on as ${side} from here`;
  els.playOn.title = `Take the position after ${plies()[k].moveNo}${plies()[k].side === 'w' ? '.' : '…'}`
    + `${plies()[k].san} on against the engine`;
}

/** Nothing to step to at either end of the game, and the buttons say so. */
function markEnds() {
  const k = shownPly;
  els.start.disabled = Math.round(frame) <= 0;
  els.prev.disabled = k < 0 && Math.round(frame) <= 0;
  const last = plies().length - 1;
  els.next.disabled = k >= last;
  els.end.disabled = k >= last;
}

/**
 * Keep the current move in view in the panel.
 *
 * It follows a scrub as well as playback — the panel saying where you are is the
 * reason it is there, and a list stuck on move 1 while the board shows the mate
 * is the same drift this whole file is written to avoid. What it will not do is
 * fight somebody reading: a wheel or a swipe over the panel buys four seconds of
 * being left alone, cancelled the moment they click a move.
 *
 * Not in the narrow layout, where the panel is not a scroll container at all —
 * scrollIntoView there scrolls the PAGE, which would drag the board off screen.
 */
function follow(k) {
  if (k < 0 || narrow()) return;
  if (performance.now() - userScrolled < 4000) return;
  rows[k].scrollIntoView({ block: 'nearest', behavior: playing ? 'smooth' : 'auto' });
}

// -Infinity rather than 0: `performance.now() - 0` is small for the first four
// seconds of the page's life, so a zero here reads as "the user just scrolled" and
// silently disabled the follow on every jump made in the first moments of a visit.
let userScrolled = -Infinity;
const markScroll = () => { userScrolled = performance.now(); };

// ------------------------------------------------------------------- transport

function play() {
  if (playing) return;
  if (frame >= lastFrame) frame = 0;
  // The view is NOT handed back on play. It used to be, on the reasoning that the
  // authored shot is better than a visitor's — which is true of a video and wrong
  // here: somebody who has turned the board to look down a diagonal has said what
  // they want to watch, and taking it away the moment the game moves is the page
  // arguing with them. `Auto` is how the camera goes back.
  playing = true;
  drawn = 0;
  measuredSince = performance.now();
  setPlayButton('pause');
  // Press play in a room and everybody's game starts. `reflect` cannot carry this
  // one: pressing play does not move the playhead, it starts moving it.
  reportPlayhead();
  ensureLoop();
}

function pause() {
  if (!playing) return;
  playing = false;
  setPlayButton('play');
  reportPlayhead();
  // A view still gliding to a preset is still animating, and killing the loop
  // under it would leave the camera stopped halfway there. So is a fire on a
  // checked king: pausing on one should leave it burning, not freeze it.
  if (!boardCam.live() && !glowing()) stopLoop();
  showStats();
}

/**
 * The play button, as a shape and a word rather than a word alone.
 *
 * Held in a variable rather than read back off the label: what the button says
 * was the only record of whether the game had finished, and `textContent ===
 * 'Replay'` is a comparison that breaks the day anything about the wording moves.
 */
const PLAY_SHAPE = '<path d="M4.5 3.2v9.6L12.8 8z"></path>';
const PAUSE_SHAPE = '<path d="M4 3h3v10H4zM9 3h3v10H9z"></path>';
let playState = 'play';

function setPlayButton(next) {
  playState = next;
  els.playIcon.innerHTML = next === 'pause' ? PAUSE_SHAPE : PLAY_SHAPE;
  els.playLabel.textContent = next === 'pause' ? 'Pause' : next === 'replay' ? 'Replay' : 'Play';
}

// AND ONCE NOW, for the state the page opens in. The `<svg>` in player.html is
// deliberately empty — the shape lives here, so the button and `playState`
// cannot come to say different things — but nothing drew the first state, and
// the only other calls are inside `play`, `pause` and the end of the game. So a
// visitor who had not yet pressed anything got the word `Play` with a blank
// square beside it, which is every first visit to the page.
setPlayButton(playState);

function toggle() {
  if (playing) return pause();
  // Space during a step is a request for the game, not for that one move again.
  beat = null;
  play();
}

/**
 * The frame loop exists only while something is moving — playback or the camera
 * easing home. A page sitting paused on move 22 draws nothing and costs nothing,
 * which on a phone is the difference between reading the annotation and watching
 * the battery.
 */
function ensureLoop() {
  if (raf) return;
  // Zero rather than `performance.now()`: the loop starts its clock on its FIRST
  // TICK, not when it was armed. Arming happens inside whatever task asked for it
  // — a click, a rebuild, the boot — and charging that task's own duration to
  // playback made the first tick advance the full 0.25s clamp: a deep link to move
  // 18 arrived eight frames into the move, so the piece was already sliding before
  // the first frame was drawn.
  lastTick = 0;
  raf = requestAnimationFrame(loop);
}

function stopLoop() {
  cancelAnimationFrame(raf);
  raf = 0;
}

function loop(now) {
  // Clamped at BOTH ends, and the lower one is not defensive padding — it is a
  // bug that was measured. A requestAnimationFrame callback is handed the
  // timestamp of the frame it belongs to, which is when that frame STARTED; arm
  // the loop from inside a long task (a scene rebuild, a resize, a tier drop) and
  // the next callback's `now` can precede the `performance.now()` taken when it was
  // armed. That is a negative dt, and a negative dt runs the game backwards: a
  // deep link to move 18 set the playhead to frame 3327 and the first tick drew
  // 3300, so the arrival began by rewinding almost a second through the move
  // before it.
  //
  // The upper clamp is the familiar one — a backgrounded tab must not come back and
  // jump thirty seconds of the game — and the other end of that trade is worth
  // knowing: on a device slow enough that a frame takes longer than the clamp,
  // playback SLOWS rather than skipping, which for a chess game is the right way
  // round since the point is to see the moves. The tier ladder is what stops that
  // happening for long.
  const dt = lastTick ? clamp((now - lastTick) / 1000, 0, 0.25) : 0;
  lastTick = now;

  if (playing || beat) {
    frame += dt * fps * speed;
    if (beat && frame >= beat.end) {
      frame = beat.end;
      beat = null;
    }
    if (playing && frame >= lastFrame) {
      frame = lastFrame;
      draw();
      pause();
      stopLoop();
      setPlayButton('replay');
      return;
    }
  }
  // Standing still on a board with a fire on it is the one case where there is
  // something to draw without the playhead having moved.
  if (glowing()) {
    glowAt = (glowAt + dt) % engine.ctx.heat.loop;
    drawGlow();
  } else {
    glowAt = 0;
    draw();
  }
  watchFrames(now);
  raf = playing || beat || boardCam.live() || glowing() ? requestAnimationFrame(loop) : 0;
}

function setSpeed(rate) {
  speed = rate;
  for (const button of els.speeds.children) {
    button.setAttribute('aria-pressed', String(Number(button.dataset.rate) === rate));
  }
}

function seek(f) {
  // A seek is a position, stated exactly: whatever was being played out is over.
  beat = null;
  frame = clamp(f, 0, lastFrame);
  if (!playing) {
    draw();
    if (playState === 'replay') setPlayButton('play');
    // Landing on a check is landing on something that moves, and nothing else
    // here would ask for a second frame. The draw above is the frame the scrub
    // bar asked for, at rest; the loop takes the fire on from there.
    if (glowing()) ensureLoop();
  }
}

/**
 * Go to a move — and, unless told otherwise, play it.
 *
 * `animate` is off for a rewind. Frames are a pure function of the index, so
 * running them backwards would work and would look wrong: a strike's sparks would
 * fly inwards and a taken piece would hop back onto the board. So the backward
 * step lands on the position BEFORE the move, which pairs with the forward step
 * playing it — left then right replays a move as many times as you like.
 */
function goToPly(k, { animate = true } = {}) {
  const at = clamp(k, 0, plies().length - 1);
  const line = tl();
  frame = frameOfPly(at);
  follow(at);

  if (!animate || playing) {
    // Mid-playback a click is a jump: the game is already running the clock.
    if (playing) beat = null;
    else seek(frame);
    return;
  }

  // A mate gets the whole closing shot rather than the usual tail: stepping onto
  // the last move of a game and stopping a tenth of a second later would park on
  // a king caught halfway over.
  const tail = plies()[at]?.mate ? (engine.ctx.closingShot ?? BEAT_TAIL) : BEAT_TAIL;
  // AND THE SLOT DOES NOT CAP THE LAST MOVE. The cap is what stops a step
  // running into the next move, and the last move of a game has no next move —
  // what follows it is the hold, which is where the closing shot lives. Capping
  // it anyway was harmless while the shot was 3.9 seconds and the slots were
  // longer than that; at 5.5 it clipped the tail off Fool's Mate (5.67s of slot
  // against 6.5s wanted) and Rapport–Rogers, which is exactly the second the
  // shot spends holding on the fallen king. `beat.end` is clamped to `lastFrame`
  // below, which is the only bound the finish actually has.
  const room = at === plies().length - 1 ? Infinity : line.slot[at];
  const runs = Math.min(line.moveSec[at] + tail, room);
  beat = { end: Math.min(lastFrame, Math.ceil((line.start[at] + runs) * fps)) };
  ensureLoop();
  draw(true);
}

function step(delta) {
  const here = plyAt(Math.round(frame));
  // Stepping back from the middle of a move goes to the start of THIS move first,
  // the way a track skip-back does — the alternative silently loses the move you
  // were watching. And it does not animate: it is a rewind to the position before
  // the move, which the next forward step then plays.
  const at = here >= 0 ? Math.round(frame) - frameOfPly(here) : 0;
  if (delta < 0 && here >= 0 && at > 2) {
    return goToPly(here, { animate: false });
  }
  if (here < 0) return goToPly(0);
  // Forward from the START of a move plays THAT move rather than the next one,
  // which is the other half of the same asymmetry: a rewind leaves the playhead on
  // the position before the move, so left-then-right has to replay it rather than
  // skipping over it. Measured before this: back one, forward one, and the board
  // was two moves further on than it started.
  goToPly(delta > 0 && at <= 2 ? here : here + delta, { animate: delta > 0 });
}

// ---------------------------------------------------------------- the scrub bar

function frameFromPointer(event) {
  const box = els.track.getBoundingClientRect();
  return Math.round(lastFrame * clamp((event.clientX - box.left) / box.width, 0, 1));
}

let scrubbing = false;
els.track.addEventListener('pointerdown', (event) => {
  scrubbing = true;
  els.track.setPointerCapture(event.pointerId);
  wasPlaying = playing;
  pause();
  seek(frameFromPointer(event));
});
els.track.addEventListener('pointermove', (event) => {
  if (scrubbing) seek(frameFromPointer(event));
});
els.track.addEventListener('pointerup', () => {
  if (!scrubbing) return;
  scrubbing = false;
  if (wasPlaying) play();
});
els.track.addEventListener('keydown', (event) => {
  const keys = {
    ArrowLeft: () => step(-1), ArrowRight: () => step(1),
    ArrowDown: () => seek(frame - fps), ArrowUp: () => seek(frame + fps),
    Home: () => seek(0), End: () => seek(lastFrame),
    PageDown: () => seek(frame - fps * 10), PageUp: () => seek(frame + fps * 10),
  };
  if (!keys[event.key]) return;
  // The document handler below binds the arrows too, and both would fire —
  // stepping twice and skipping a move, which is a bug you only find by counting.
  event.preventDefault();
  event.stopPropagation();
  keys[event.key]();
});
let wasPlaying = false;

// --------------------------------------------------------------------- the view

/**
 * THE CAMERA, and every gesture that moves it: `../board-camera.js`.
 *
 * It used to be six hundred lines here — `held`, `want`, the damped walk between
 * them, the drag, the pan, the wheel, the pinch, the preset toolbar and the write
 * into `ctx.camTrack`. The play pages had their own set of all of it, and the two
 * did not match: the same drag turned the board at two speeds, the wheel went a
 * third further per notch over there, and the board stopped tipping at 78 degrees
 * on one page and 87 on the other. So it moved out to where both can have it, and
 * that file's header keeps the account of what each part is for.
 *
 * WHAT STAYED HERE is everything that is a WATCH page's rather than a board's:
 * `Auto`, which only means something where there is a shot list; the angle in the
 * address bar; and the frame the camera is measured against, which on this page
 * is the playhead.
 *
 * `drive: 'host'` because this page already has a frame loop running for
 * playback, and stepping the camera from inside it is one loop instead of two —
 * see `draw`, which calls `step` and draws the result in the same frame.
 */
const boardCam = mountBoardCamera({
  engine,
  canvas,
  views: VIEWS,
  viewsBox: els.views,
  drive: 'host',
  start: 'angled',
  frame: () => rendered,
  // Not a render. Drawing straight from a pointer handler puts a whole frame on
  // the queue per mouse event, and a 1000Hz mouse sends those far faster than any
  // GPU drains them, so the picture falls behind the hand in bursts. The loop
  // draws once per display refresh from wherever the target has got to by then.
  onChange: () => { ensureLoop(); syncViewUrl(); },
  onDraw: () => draw(true),
  // Going to or from Auto decides whether `Scripted shots` in the panel is doing
  // anything, and the panel has no way to know that on its own.
  onView: () => { syncViewUrl(); viewPanel?.refresh(); },
  // The same road as the View panel's own control for it — stored, dropped from
  // the address bar, and the scene rebuilt with the playhead kept on its move.
  onDimension: (value) => {
    applyParam('dimension', engine.specs.dimension, value);
    viewPanel?.refresh();
    placeForcesLegend();
  },
});

/**
 * Maximising, shared with the play pages — see `chess-app/maximise.js` for what
 * it takes over and why it is not simply `stageBox.requestFullscreen()`.
 *
 * Wired here rather than down with the other listeners because the sizing below
 * asks it whether the board is maximised, and a render sized against the wrong
 * box is the bug it exists to avoid.
 */
const full = mountMaximise(() => applyStageSize(true));

// ------------------------------------------------------------ size and quality

let drawn = 0;
let measuredSince = 0;

/**
 * How large the board is drawn, and how many pixels go into it:
 * `../stage-size.js`, shared with the play pages and the puzzles page.
 *
 * The ladder ABOVE the budget is still this page's, because it is measured rather
 * than guessed — see `watchFrames`, which counts frames actually drawn against
 * frames that should have been and steps down a rung when playback is short. No
 * other page has playback to measure, so no other page can do that; what they all
 * share is what a rung is worth in pixels.
 */
const sizing = mountStageSize({
  box: stageBox,
  canvas,
  maxed: () => full.maxed(),
  budget: () => TIERS[tierIx].pixels,
  // `?stage=native` renders exactly what the video renders, which is the whole
  // point of it — no fitting to the window, no budget.
  fixed: () => (nativeStage ? [meta.width, meta.height] : null),
  onResize: (w, h) => {
    engine.setStage(w, h);
    // A rebuild leaves the canvas holding nothing until something is drawn into
    // it, and it also replaces ctx.timeline — so the list and the bar are re-read.
    rendered = -1;
    shownPly = -2;
    draw(true);
    showStats();
  },
});

/**
 * The box changed without the window doing — maximising, a rebuild, a tier drop.
 *
 * A declaration rather than an arrow so it is hoisted above `mountMaximise`,
 * which is handed it a few lines up and would otherwise be reaching into a
 * temporal dead zone to get it.
 */
function applyStageSize(force = false) {
  sizing.fit(force);
}

/**
 * Step down a tier when playback is measurably dropping frames.
 *
 * Measured as frames actually drawn against frames that should have been drawn,
 * over a second and a half of playing — which is the thing a visitor sees, and
 * unlike a CPU timing of renderFrame it includes the GPU, where the cost of a
 * shadow map at 3072 actually lands.
 *
 * Only below 2x: at 2x the scene wants 60 frames a second and a 60Hz display
 * cannot deliver a frame more than that, so the measurement would report a
 * shortfall that is the monitor's rather than the renderer's.
 */
function watchFrames(now) {
  if (!playing || speed > 1 || nativeStage || manualQuality) return;
  const elapsed = (now - measuredSince) / 1000;
  if (elapsed < 1.5) return;
  const want = fps * speed * elapsed;
  const got = drawn;
  drawn = 0;
  measuredSince = now;
  showStats(got / elapsed);
  if (got >= want * DROP_RATIO || tierIx >= TIERS.length - 1) return;
  tierIx++;
  stageIsSlow = true;
  // `engine.params` IS `ctx.params`, and `quality` is read in setup() — so the
  // rebuild that setStage does is what picks the new shadow map up.
  engine.params.quality = TIERS[tierIx].quality;
  console.info(`chess-app: ${(got / elapsed).toFixed(1)}fps of ${fps * speed}`
    + ` — dropping to the "${TIERS[tierIx].name}" tier.`);
  applyStageSize(true);
  // The panel is showing a quality the scene no longer has.
  viewPanel?.refresh();
}

function showStats(measured) {
  if (!wantStats) return;
  const [w, h] = sizing.size();
  els.status.textContent = `${w}×${h} · ${TIERS[tierIx].name}`
    + (measured ? ` · ${measured.toFixed(0)}fps` : '')
    + (stageIsSlow ? ' · stepped down' : '');
}

// --------------------------------------------------------------- context menu

// The stage is dragged to look around and everything else is a button, so the
// browser's own menu only ever shows up here by accident: a slipped
// right-click, or a long press on a touch screen part-way through a drag. Text
// fields keep theirs, where copy and paste are worth having.
document.addEventListener('contextmenu', (event) => {
  if (event.target.closest?.('input, textarea, [contenteditable]')) return;
  event.preventDefault();
});

// ------------------------------------------------------------------- keyboard

document.addEventListener('keydown', (event) => {
  // Ctrl+C is copy, not Forces; Ctrl+F is find, not fullscreen. And a dialog
  // (the share sheet) has its own keys: the board behind it should not move.
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  if (event.target.matches('input, select, textarea') || event.target.closest?.('dialog[open]')) return;
  // While the palette is open the arrows and space belong to it, not the board.
  if (!els.palette.hidden) {
    if (event.key === 'Escape') { closePalette(); event.preventDefault(); }
    return;
  }
  if (event.key === '/') { event.preventDefault(); openPalette(); return; }
  const keys = {
    ' ': () => toggle(),
    k: () => toggle(),
    ArrowLeft: () => step(-1),
    ArrowRight: () => step(1),
    Home: () => seek(0),
    End: () => seek(lastFrame),
    // Native fullscreen is left by `esc` before the page ever sees the key. The
    // fallback has to do it here — and leaving is what `esc` means while
    // something is over the page, before it means anything about the camera.
    Escape: () => (full.maxed() ? full.set(false) : boardCam.setView('angled')),
    f: () => full.toggle(),
    // The same key the beginner's page uses for the same thing.
    v: () => setVoice(!voice.on),
    m: () => toggleSound(),
    c: () => cycleForces(),
    w: () => openTogether(),
    ',': () => setSpeed(SPEEDS[Math.max(0, SPEEDS.indexOf(speed) - 1)]),
    '.': () => setSpeed(SPEEDS[Math.min(SPEEDS.length - 1, SPEEDS.indexOf(speed) + 1)]),
  };
  const act = keys[event.key];
  if (!act) return;
  event.preventDefault();
  act();
});

els.play.addEventListener('click', toggle);
els.prev.addEventListener('click', () => step(-1));
els.next.addEventListener('click', () => step(1));
// Home and End have worked on the keyboard since the first version; nothing on
// screen said so, and the two ends of a game are exactly what somebody reaches
// for after watching it once.
els.start.addEventListener('click', () => { pause(); seek(0); });
els.end.addEventListener('click', () => { pause(); seek(lastFrame); });

// -------------------------------------------------------------------- theme

/**
 * Light or dark, shared with the library page through one localStorage key.
 *
 * The stage does not follow, and that is deliberate — see the note at the top of
 * controls.css. What changes is the chrome around the board.
 */

/**
 * The theme, on the page and in the render.
 *
 * Both halves or neither. The chrome is CSS tokens; the paper the board stands
 * on is drawn inside the frame by the scene and can only be reached by telling
 * it. A theme that moved one and not the other is what made the stage read as a
 * hole cut in a light page — see `chess-app/theme.js`.
 */
function setTheme(theme, remember) {
  const light = theme === 'light';
  document.documentElement.dataset.theme = light ? 'light' : 'dark';
  paintStage(engine, light);
  els.themeIcon.innerHTML = light ? MOON : SUN;
  els.theme.setAttribute('aria-label', light ? 'Switch to dark mode' : 'Switch to light mode');
  els.theme.title = light ? 'Dark mode' : 'Light mode';
  $('theme-color')?.setAttribute('content', light ? '#f7f5f0' : '#0a0b0d');
  if (remember) {
    try { localStorage.setItem('immortal-theme', light ? 'light' : 'dark'); } catch { /* private mode */ }
  }
}

els.theme.addEventListener('click', () => {
  setTheme(document.documentElement.dataset.theme === 'light' ? 'dark' : 'light', true);
  // Same reason as the play page: the paper is a live parameter and this page is
  // usually paused, so nothing else would ask for the frame that shows it.
  draw(true);
});
/*
 * The inline script in the head already chose; this only dresses the button.
 *
 * EXCEPT UNDER `?video=1`, WHICH IS DARK WHATEVER THE VISITOR PREFERS. The flag
 * means "render exactly what the upload renders", and the upload is rendered by
 * `animations/207-famous-chess-games/` — a page with no theme, no `theme.js` and
 * therefore the scene's own stage: the dark row in `STAGE`. A light-mode visitor
 * opening `?video=1` was getting the video's camera, the video's timing and the
 * video's chrome on pale paper, which is the one thing about the picture the
 * video does not have.
 *
 * IT IS ALSO WHAT MAKES `scripts/check-player.mjs` MEAN ANYTHING. That check
 * opens the capture page and this page side by side and compares the frames pixel
 * for pixel, and its whole premise is that `?video=1` drops every default the app
 * applies of its own. The theme was the one it did not drop, so every frame of
 * every game differed — at about 7dB, which is not drift, it is a different
 * picture — and the check had been reporting a real difference that read like a
 * broken renderer. See the note on `STAGE` in `chess-app/theme.js` for why the
 * paper has to be told to the scene rather than styled.
 */
setTheme(videoMode ? 'dark' : (document.documentElement.dataset.theme || 'dark'), false);

// ------------------------------------------------------------------ the view

/**
 * Parameters this page owns, which are therefore not controls.
 *
 * Short on purpose. `game` is the header picker; `orientation` is decided by the
 * shape of the window and `duration` by the length of the game (see
 * `PLY_SECONDS`), with the speed buttons over the top of it — and a control for
 * it would move `totalFrames` under the playhead; `moveList`, `progressBar` and
 * `panel` are the video's own chrome, which this page replaces with the real
 * thing — and `panel` is the column those two would sit in, so a control for it
 * would offer a visitor a third of the board's canvas in exchange for an empty
 * column, since the things it holds are hidden two lines above.
 */
// `narration` is the sixth, and it is hidden as well as defaulted off in
// `APP_DEFAULTS`: the page keeps the voice it can pace and neither shows nor plays
// the one it cannot. The default is what silences it — this set only means there
// is no slider for it in the View tab.
const PAGE_OWNS = new Set(['game', 'orientation', 'duration', 'panel', 'moveList', 'progressBar', 'narration']);

/**
 * Everything the View tab does not show: the five above, and the camera.
 *
 * THE CAMERA IS NOT A PANEL ON THIS PAGE — IT IS THE BOARD.
 * The scene's seven camera parameters are how a shot is COMPOSED, and they are
 * the right controls for somebody directing a video. Here the camera is in the
 * visitor's hands: the toolbar over the board sets the angle, a drag turns it, a
 * wheel or two fingers frame it, and `Escape` puts it back. Against that, three
 * of the seven were already dead — `Scripted shots`, `Camera height` and
 * `Steadiness` all describe a baked track that a held view replaces, and the
 * panel was reduced to printing a line under each saying so — and the other four
 * were a second, worse way to do what the board already does under the pointer.
 *
 * A group of seven controls, three of them disabled and four of them redundant,
 * is not a feature. It is the panel's whole promise ("everything the render is
 * made of") failing in the one place a visitor is most likely to look first.
 *
 * Derived from the group rather than listed by key, so a camera parameter the
 * scene grows tomorrow is hidden with the rest of them instead of appearing here
 * on its own. Still reachable: `?tilt=60` and the rest are the scene's URL
 * interface and are untouched, which is what `capture.js` and a shared link from
 * the studio use.
 */
function hiddenParams() {
  const keys = new Set(PAGE_OWNS);
  for (const [key, spec] of Object.entries(engine.specs)) {
    if (spec.group === 'Camera') keys.add(key);
  }
  return keys;
}

// Which parameters the visitor has actually reached into. Only these go in the
// URL — a link should carry what somebody chose, not a transcript of every
// default the scene happens to have.
const touched = new Set();
// A quality chosen by hand switches the ladder off. It is there to rescue a
// visitor who never opened the panel; someone who asked for `high` has chosen,
// and being overruled two seconds later is worse than a slower frame rate.
let manualQuality = false;

let viewPanel;

/** How many parameters the visitor has reached into, on the tab itself. */
function markTouched() {
  els.tabViewN.textContent = touched.size ? String(touched.size) : '';
  els.tabViewN.classList.toggle('changed', touched.size > 0);
}

/**
 * The finishes, in a word each.
 *
 * The scene's own labels — "lacquered wood", "polished marble", "frosted glass"
 * — say what the material is and are the right length for a menu inside a panel.
 * In a 52px header they are not, and the adjective is the part that can go: the
 * whole label is on the button as its title, and the View tab still spells it
 * out in full.
 *
 * A finish the scene grows tomorrow and this does not name still gets a button,
 * under its own label. A header that silently dropped one would be worse than a
 * long word in it — the same rule the View tab follows for a group it has no
 * heading for.
 */
const FINISH_WORD = { wood: 'Wood', marble: 'Marble', glass: 'Glass' };

/**
 * And the two sets of men, for the same reason: "Staunton (the standard set)" is
 * a menu's label and "carved figures" is two words where the header has room for
 * one. Both are on the button as its title, and the View tab spells them out.
 */
const STYLE_WORD = { club: 'Club', figures: 'Figures', staunton: 'Staunton' };

/** What to re-light after any parameter changes, wherever it changed from. */
const quickMarks = [];

/**
 * Which button in the header strip is lit.
 *
 * Called after every change rather than only after one made here: the View tab
 * sets the same two parameters, and a strip that went on showing the old set
 * would be a second, wrong answer to a question the page has already answered.
 */
function markQuick() {
  for (const mark of quickMarks) mark();
}

/**
 * The set and the men, in the header.
 *
 * WHY THESE TWO AND NOT THE OTHER FORTY
 * The View tab is right for forty-two parameters and wrong for the two that are
 * not really settings — the board a game is played on and what the men are made
 * of are the LOOK of the thing, they are what somebody changes first, and they
 * are worth seeing the effect of immediately rather than through a tab, a fold
 * and a scroll. Everything else stays where it is.
 *
 * IT IS THE SAME CONTROL, NOT A COPY OF IT
 * Both clusters are built from `engine.specs`, like the panel, so a scene that
 * renames a set or grows a fourth finish is followed here rather than
 * contradicted. Both go down `applyParam`, which is the one road a value takes
 * to the engine and to the address bar. And both tell the panel to catch up
 * afterwards, so there is still exactly one place the value lives — see
 * `pickQuick`. If either parameter ever stops existing, its cluster simply is
 * not built.
 */
function buildQuick() {
  const specs = engine.specs;
  if (specs.board) els.quick.append(quickPart('board', 'Board', boardPicker(specs.board)));
  if (specs.men) els.quick.append(quickPart('men', 'Pieces', segmented('men', specs.men, STYLE_WORD)));
  if (specs.finish) els.quick.append(quickPart('finish', 'Finish', segmented('finish', specs.finish, FINISH_WORD)));
  markQuick();
}

/**
 * One cluster of the strip: the word that says what it is, and the control.
 *
 * Wrapped rather than appended loose so the two can go together when the window
 * narrows — a heading with nothing under it was what the old `:first-child` rule
 * in the stylesheet was working around. `data-part` is what those rules name.
 */
function quickPart(name, tag, control) {
  const el = document.createElement('div');
  el.className = 'quick-part';
  el.dataset.part = name;
  const label = document.createElement('span');
  label.className = 'quick-tag';
  label.textContent = tag;
  el.append(label, control);
  return el;
}

/**
 * A cluster of two or three options, as one short word each.
 *
 * The same segmented group the points of view over the board are, and the same
 * one the finish has had since there was a strip at all — this only stopped
 * being written out twice when the men wanted one too.
 */
function segmented(key, spec, words) {
  const group = document.createElement('div');
  group.className = 'quick-seg';
  group.setAttribute('role', 'group');
  group.setAttribute('aria-label', spec.label);
  for (const option of spec.options) {
    const value = option.value ?? option;
    const label = option.label ?? option;
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = words[value] || label;
    button.title = label;
    button.addEventListener('click', () => pickQuick(key, spec, value));
    group.appendChild(button);
    quickMarks.push(() => button.setAttribute('aria-pressed', String(engine.params[key] === value)));
  }
  return group;
}

/**
 * THE BOARD, AS A DROPDOWN RATHER THAN A ROW OF SWATCHES.
 *
 * A row was right when there were ten sets: ten small squares of colour a
 * visitor could take in at a glance and hit in one click, which is why they are
 * still drawn as squares in the View tab. There are twenty-odd now, and
 * twenty-odd of them is three hundred pixels of a fifty-two pixel header — it
 * pushed the search box off the end of a laptop, and it had stopped reading as a
 * control and started reading as a paint chart.
 *
 * So it folds, and what it keeps is the part the row was FOR: the button says
 * which set is on in that set's own colours, and every line of the menu is drawn
 * as its four squares beside its name, because "rosewood & cream" is a colour you
 * have to imagine. Same `setSwatch` the panel's grid uses, so there is one
 * drawing of what a chess set looks like on this site.
 *
 * "The game's own" and "custom" have no colours of their own to draw, so they
 * borrow the ones the scene resolved them TO — which is what is on the board
 * either way, and a truer answer than a word saying there is nothing to show.
 */
function boardPicker(spec) {
  const byId = new Map(BOARDS.map((set) => [set.id, set]));
  const colours = (value) => byId.get(value) || ctx().set;

  const wrap = document.createElement('div');
  wrap.className = 'quick-pick';

  const now = document.createElement('button');
  now.type = 'button';
  now.className = 'quick-now';
  now.title = spec.label;
  now.setAttribute('aria-haspopup', 'true');
  now.setAttribute('aria-expanded', 'false');

  const menu = document.createElement('div');
  menu.className = 'quick-menu';
  menu.hidden = true;

  const rows = [];
  for (const option of spec.options) {
    const value = option.value ?? option;
    const label = option.label ?? option;
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'quick-row';
    row.addEventListener('click', () => {
      pickQuick('board', spec, value);
      open(false);
    });
    menu.appendChild(row);
    rows.push({ row, value, label });
  }

  wrap.append(now, menu);

  now.addEventListener('click', () => open(menu.hidden));
  // The document's own key handler binds the arrows to the playhead and space to
  // play, and neither belongs to the board while this is open — see the note on
  // `stopPropagation` there. Enter is nobody else's, so it needs nothing.
  wrap.addEventListener('keydown', (event) => {
    const at = rows.findIndex((r) => r.row === document.activeElement);
    const go = (i) => rows[(i + rows.length) % rows.length].row.focus();
    if (event.key === 'Escape') { open(false); now.focus(); }
    else if (event.key === 'ArrowDown') go(at + 1);
    else if (event.key === 'ArrowUp') go(at < 0 ? -1 : at - 1);
    else if (event.key === 'Home') go(0);
    else if (event.key === 'End') go(-1);
    else if (event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
  });
  // A press anywhere else closes it. `mousedown` rather than `click` so it goes
  // away as the press lands, and on the document so nothing has to be told about
  // it; a press on the button itself is the toggle above.
  document.addEventListener('mousedown', (event) => {
    if (!menu.hidden && !wrap.contains(event.target)) open(false);
  });

  quickMarks.push(() => {
    const value = engine.params.board;
    const option = rows.find((r) => r.value === value);
    now.innerHTML = `${setSwatch(colours(value))}<span class="quick-set">`
      + `${escapeHtml(setName(option?.label ?? value))}</span>`
      + '<svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor"'
      + ' stroke-width="1.7" stroke-linecap="round" aria-hidden="true"><path d="M2.5 4.5L6 8l3.5-3.5"></path></svg>';
    for (const row of rows) {
      row.row.setAttribute('aria-current', String(row.value === value));
      row.row.innerHTML = `${setSwatch(colours(row.value))}`
        + `<span>${escapeHtml(setName(row.label))}</span>`;
    }
  });

  return wrap;

  function open(on) {
    menu.hidden = !on;
    now.setAttribute('aria-expanded', String(on));
    if (!on) return;
    // Opened on what is already chosen, so the arrows walk from there and a
    // second press of Enter changes nothing.
    (rows.find((r) => r.value === engine.params.board) || rows[0])?.row.focus();
  }
}

/** "custom (the colours below)" is a menu's sentence; a strip has room for a name. */
function setName(label) {
  return String(label).replace(/\s*\(.*\)$/, '');
}

/**
 * Take a value from the header strip.
 *
 * Straight down `applyParam`: same coercion, same redraw, same line in the
 * address bar, same entry in the panel's "You changed" list, so a set chosen up
 * here can be put back down there. The panel is then told to catch up, because
 * it is showing these two controls as well and `applyParam` does not refresh it
 * — the panel normally refreshes itself, having been the one that called.
 */
function pickQuick(key, spec, value) {
  applyParam(key, spec, value);
  viewPanel?.refresh();
}

function mountView() {
  // Anything the visitor arrived with counts as theirs: they may have followed
  // somebody's link to a glass set on ebony, and Reset has to be able to undo it.
  const hide = hiddenParams();
  // The first moment the specs are known — the store is read before the scene is
  // built, which is the whole point of it — and so the moment to throw away a key
  // nothing can read any more. `PAGE_SPECS`'s own keys survive it; see `PAGE_KEYS`
  // in the store, which is what makes one shared store safe to prune from six
  // different pages.
  store.prune(engine.specs);
  // A URL and the store both count as things the visitor chose — one of them said
  // it in a link and the other said it last time. Hidden parameters do not: there
  // is no control to put them back with, so nothing should offer to.
  for (const key of [...app.keys(), ...Object.keys(prefs)]) {
    if (PAGE_SPECS[key] || (engine.specs[key] && !hide.has(key))) touched.add(key);
  }
  viewPanel = mountViewPanel({
    engine,
    root: els.view,
    hide,
    touched,
    boards: BOARDS,
    context: () => ({ auto: !boardCam.held }),
    extras: PAGE_SPECS,
    onChange: applyParam,
    onReset: resetView,
    onResetOne: resetOne,
    // The panel's size chips move the masthead, the bar and the panel itself,
    // so the stage's box has changed shape. `applyStageSize(true)` is the same
    // call the quality ladder and the maximise button make.
    onChrome: () => applyStageSize(true),
  });
  markTouched();

  // And the mode switch at the top of the panel, which takes the speeds, the
  // phase strip, the quick strip and four camera presets off the page — every
  // one of them by stylesheet, so all that is left for the page is the same
  // re-fit: the bar and the masthead are now different heights.
  onMode(() => applyStageSize(true));

  const tabs = [[els.tabMoves, els.moves], [els.tabView, els.view]];
  for (const [tab, panel] of tabs) {
    tab.addEventListener('click', () => {
      for (const [other, pane] of tabs) {
        const on = other === tab;
        other.setAttribute('aria-selected', String(on));
        pane.hidden = !on;
      }
      // The phase strip jumps the playhead, which is the move list's business.
      els.phaseJump.hidden = panel !== els.moves;
      // The list follows the playhead only while it is on screen, so coming back
      // to it should not leave the current move somewhere above the scroll.
      if (panel === els.moves) { userScrolled = -Infinity; follow(shownPly); }
    });
  }
}

/**
 * Take one parameter from the panel.
 *
 * `setParams` in the engine coerces it and rebuilds if it has to — the same road
 * a URL parameter and a `capture.js --set` take. What is left for here is what
 * that means to the page: a live parameter needs the frame drawn again, and a
 * rebuild has replaced `ctx.timeline`, so the scrub bar's landmarks are rebuilt
 * from the new one before anything is drawn against it.
 */
function applyParam(key, spec, value) {
  // The page's own settings share the panel and this one road into it, and stop
  // here: there is no parameter for the engine to take and nothing in the picture
  // to redraw. See `PAGE_SPECS`.
  if (PAGE_SPECS[key]) {
    setPage(key, value);
    touched.add(key);
    markTouched();
    rememberPref(key);
    dropFromUrl(key);
    return;
  }

  // Where we are, in terms that survive the clock being redivided under us. See
  // `afterRebuild`.
  const held = holding();
  engine.setParams({ [key]: value });

  if (key === 'quality') {
    manualQuality = true;
    const at = TIERS.findIndex((tier) => tier.quality === engine.params.quality);
    if (at >= 0) tierIx = at;
    applyStageSize(true);
  } else if (spec.live) {
    draw(true);
  } else {
    afterRebuild(held);
  }

  touched.add(key);
  markTouched();
  markQuick();
  paintPlates();
  if (key === 'forces') paintForcesButton();
  if (key === 'dimension') placeForcesLegend();
  rememberPref(key);
  dropFromUrl(key);
  showStats();
}

/**
 * Take one of the page's own settings, wherever it came from — the panel, a URL
 * on arrival, or Reset putting it back.
 *
 * Separate from `applyParam` because the two callers want different halves of
 * it: a change is also a thing the visitor TOUCHED and belongs in the address
 * bar, and a reset is the opposite of both.
 */
function setPage(key, value) {
  if (key === 'plates') {
    cornerPlates = Boolean(value);
    showPlates();
  }
}

/**
 * Where the playhead is, as a move and a distance into it.
 *
 * A frame number is only meaningful against a particular timeline, and `emphasis`
 * redivides the clock: at emphasis 1 the four moves this game is watched for take
 * two to three times a plain move, at 0 every move gets the same slice, and every
 * start time in the game moves. So the frame that was showing 18.Bd6 shows
 * something else afterwards — measured, before this existed: the playhead sat
 * still at frame 3327 and the board went back to move 15.
 *
 * A move and an offset into it survives that. It also costs nothing when the
 * timeline did NOT move — `start[ply] + into` is the frame we were already on —
 * so this is not a special case for one parameter.
 */
function holding() {
  const at = plyAt(rendered);
  return at < 0 ? null : { ply: at, into: rendered / fps - tl().start[at] };
}

/** Everything the page derives from a scene that has just been rebuilt. */
function afterRebuild(held) {
  rendered = -1;
  shownPly = -2;
  // `emphasis` moves every landmark on the bar, and the phase spans with them.
  buildTrack();
  review?.redraw();
  if (held) frame = clamp(Math.round((tl().start[held.ply] + held.into) * fps), 0, lastFrame);
  draw(true);
}

/**
 * Put the changed parameters in the address bar.
 *
 * So a set somebody liked survives a reload and travels with the link, next to
 * `game` and `move`. Only what differs from the scene's own default is written,
 * and only what was touched by hand — the tier the ladder chose is a fact about a
 * device, not a preference to share.
 */
/**
 * Take a parameter OUT of the address bar, and never put one in.
 *
 * A CHANGED PARAMETER IS DROPPED RATHER THAN UPDATED, and that is the whole of
 * what this is for. Preferences live in the store (see `prefs`) and not in the
 * link — but a visitor can still ARRIVE on `?board=slate`, and if they then pick
 * a different set, that URL has become a lie: the picture says one thing, the
 * address bar says another, and a refresh would quietly undo what they just did,
 * because a URL outranks the store on the way in. Dropping the key leaves the
 * address bar saying nothing about the board, which is true, and lets the store
 * answer on the next visit, which is what they chose.
 *
 * The playhead and the camera are not settings and are not touched: `?move=18`
 * and `?view=-9,44,0.94` are where you are and what you are looking at, they are
 * worth sharing, and `syncUrl` and `syncViewUrl` go on writing them.
 */
function dropFromUrl(...keys) {
  const query = new URLSearchParams(location.search);
  let went = false;
  for (const key of keys) {
    if (query.has(key)) { query.delete(key); went = true; }
  }
  if (!went) return;
  const rest = String(query);
  history.replaceState(null, '', rest ? `${location.pathname}?${rest}` : location.pathname);
}

/**
 * Put one parameter back, without touching the rest.
 *
 * Reset was all or nothing, which is a blunt answer to "I liked the glass but not
 * the camera height". The panel's changed list is where this is reachable from.
 */
function resetOne(key) {
  const page = PAGE_SPECS[key];
  const spec = page || engine.specs[key];
  const at = page ? null : holding();
  dropFromUrl(key);
  touched.delete(key);
  markTouched();
  store.drop(key);

  if (page) {
    // Nothing was rendered from it, so there is nothing to redraw.
    setPage(key, pageDefault(key, spec));
  } else if (key === 'quality') {
    // Back to the ladder's reading of the machine rather than the scene's
    // default, for the reason resetView gives.
    manualQuality = false;
    tierIx = pickTier();
    engine.setParams({ quality: TIERS[tierIx].quality });
    applyStageSize(true);
    afterRebuild(at);
  } else {
    // The page's default, not the scene's: Reset means "how this page arrives",
    // and on this page the men arrive Staunton. See `pageDefault`.
    engine.setParams({ [key]: pageDefault(key, spec) });
    if (spec.live) draw(true);
    else afterRebuild(at);
  }
  markQuick();
  showStats();
}

function resetView() {
  const patch = {};
  // Before `touched` is emptied — it is the only record of which keys in that URL
  // belong to this panel, and clearing it first would leave every one of them in
  // the address bar with the board back at its defaults.
  dropFromUrl(...touched);
  for (const key of touched) {
    const spec = PAGE_SPECS[key] || engine.specs[key];
    if (PAGE_SPECS[key]) setPage(key, pageDefault(key, spec));
    else patch[key] = pageDefault(key, spec);
  }
  touched.clear();
  markTouched();
  // Nothing left that anybody chose, so nothing left to remember.
  store.forget();

  manualQuality = false;
  // Not the scene's default: the tier is this page's own reading of the machine,
  // and a reset should hand quality back to it rather than pin it at `high`.
  tierIx = pickTier();
  patch.quality = TIERS[tierIx].quality;
  const held = holding();
  engine.setParams(patch);
  markQuick();
  applyStageSize(true);
  afterRebuild(held);
}

// ------------------------------------------------------------------ searching

/**
 * Search, from inside a game.
 *
 * The header used to hold a <select> of every title, which is a fine control for
 * ten games and a bad one for sixty — and it could only ever answer one question,
 * "which game", when the question somebody on a watch page actually has is more
 * often "where was the bit about the queen".
 *
 * So this looks in two places and says which is which. A hit INSIDE this game is
 * a jump: the playhead moves and nothing else does, so the angle you set and the
 * parameters you changed are all still there. A hit elsewhere is a page load, and
 * a filter is the library page with that filter already on.
 *
 * Nothing typed yet is not an empty state: it is the phases of this game and the
 * rest of the library, which is what the <select> used to be plus the thing it
 * could not do.
 */
const fold = (s) => String(s).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

/** The query, marked inside a value that contains it. */
function marked(value, query) {
  const text = escapeHtml(value);
  if (!query) return text;
  // Folding is only length-preserving for precomposed accents; rather than mark
  // the wrong letters when it is not, don't mark.
  if (fold(value).length !== value.length) return text;
  const at = fold(value).indexOf(fold(query));
  if (at < 0) return text;
  return escapeHtml(value.slice(0, at))
    + `<mark>${escapeHtml(value.slice(at, at + query.length))}</mark>`
    + escapeHtml(value.slice(at + query.length));
}

/** The library's own filters, as somewhere a search can send you. */
function libraryFilters(q) {
  const out = [];
  const push = (param, label, values) => {
    const counts = new Map();
    for (const row of INDEX) {
      for (const value of values(row)) {
        if (fold(value).includes(q)) counts.set(value, (counts.get(value) || 0) + 1);
      }
    }
    for (const [value, n] of [...counts].sort((a, b) => b[1] - a[1]).slice(0, 3)) {
      out.push({ kind: 'filter', param, label, value, n });
    }
  };
  push('player', 'Player', (r) => r.players);
  push('family', 'Opening', (r) => [r.family]);
  push('era', 'Era', (r) => [r.era]);
  return out;
}

function paletteSearch(query) {
  const q = fold(query.trim());
  const g = meta207();
  const line = tl();
  const groups = [];

  if (!q) {
    groups.push({
      label: `In ${g.title}`, here: true,
      items: line.phaseSpan.map((phase, i) => ({ kind: 'phase', i, phase })),
    });
    groups.push({
      label: 'The rest of the library',
      items: INDEX.filter((row) => row.id !== gameId).map((row) => ({ kind: 'game', row })),
    });
    return groups;
  }

  // This game: the score, and the annotation written for it.
  const here = [];
  plies().forEach((ply, k) => {
    const note = g.notes[k] || {};
    const hay = [ply.san, note.lead || '', note.note || ''];
    const at = hay.findIndex((text) => fold(text).includes(q));
    if (at < 0) return;
    here.push({ kind: 'move', k, ply, text: at === 0 ? (note.lead || ply.san) : hay[at] });
  });
  for (const [i, phase] of line.phaseSpan.entries()) {
    if (fold(phase.title).includes(q)) here.push({ kind: 'phase', i, phase });
  }
  if (here.length) groups.push({ label: `In ${g.title}`, here: true, items: here.slice(0, 8) });

  const games = INDEX.filter((row) => row.id !== gameId && fold(
    [row.title, row.white, row.black, row.event, row.opening, row.date, row.blurb].join(' '),
  ).includes(q));
  if (games.length) {
    groups.push({ label: 'Elsewhere in the library', items: games.slice(0, 5).map((row) => ({ kind: 'game', row })) });
  }

  const filters = libraryFilters(q);
  if (filters.length) groups.push({ label: 'Filter the library instead', items: filters });

  return groups;
}

let paletteGroups = [];
let paletteItems = [];
let paletteCursor = 0;

function renderPalette() {
  const query = els.paletteInput.value.trim();
  paletteGroups = paletteSearch(query);
  paletteItems = paletteGroups.flatMap((group) => group.items);
  paletteCursor = Math.min(paletteCursor, Math.max(0, paletteItems.length - 1));

  els.paletteN.textContent = query
    ? `${paletteItems.length} match${paletteItems.length === 1 ? '' : 'es'}`
    : '';

  if (!paletteItems.length) {
    els.paletteBody.innerHTML = '<p class="palette-none">Nothing in this game or the library matches that.</p>';
    els.paletteVerb.textContent = 'jump';
    return;
  }

  const html = [];
  let i = 0;
  for (const group of paletteGroups) {
    html.push(`<div class="palette-group${group.here ? ' here' : ''}">`
      + `${escapeHtml(group.label)} &middot; ${group.items.length}</div>`);
    for (const item of group.items) {
      const on = i === paletteCursor;
      const head = `<button type="button" class="palette-hit" role="option" data-i="${i}" aria-selected="${on}">`;
      if (item.kind === 'move') {
        html.push(head
          + `<span class="move ${item.ply.side}">${item.ply.moveNo}${item.ply.side === 'w' ? '.' : '&hellip;'}`
          + `${escapeHtml(item.ply.san)}</span>`
          + `<span class="what">${marked(item.text, query)}</span></button>`);
      } else if (item.kind === 'phase') {
        html.push(head
          + '<span class="move"><svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"'
          + ' stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M2 8h12M5 4.5h6M6.5 11.5h3"></path></svg></span>'
          + `<span class="what">${marked(item.phase.title, query)}</span>`
          + `<span class="n">from move ${plies()[item.phase.from].moveNo}</span></button>`);
      } else if (item.kind === 'game') {
        html.push(head
          + `<span class="when">${escapeHtml(item.row.date)}</span>`
          + `<span class="what"><span class="name">${marked(item.row.title, query)}</span>`
          + `<span class="sub">${escapeHtml(item.row.white)} &mdash; ${escapeHtml(item.row.black)}</span></span>`
          + `<span class="n">${item.row.plies} half-moves</span></button>`);
      } else {
        html.push(head
          + `<span class="when">${escapeHtml(item.label)}</span>`
          + `<span class="what">${marked(item.value, query)}</span>`
          + `<span class="n">${item.n} game${item.n === 1 ? '' : 's'}</span></button>`);
      }
      i += 1;
    }
  }
  els.paletteBody.innerHTML = html.join('');

  const kind = paletteItems[paletteCursor]?.kind;
  els.paletteVerb.textContent = kind === 'move' || kind === 'phase' ? 'jump to it'
    : kind === 'game' ? 'watch it' : 'filter the library';
  els.paletteBody.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
}

function takePalette(item) {
  if (!item) return;
  if (item.kind === 'move' || item.kind === 'phase') {
    // A jump, not a load: the camera, the speed and every parameter stay put.
    closePalette();
    userScrolled = 0;
    goToPly(item.kind === 'move' ? item.k : item.phase.from, { animate: false });
    return;
  }
  if (item.kind === 'game') { location.href = takeRoomTo(watchUrl(item.row.id)); return; }
  const query = new URLSearchParams();
  query.set(item.param, item.value);
  location.href = `/?${query}`;
}

function openPalette() {
  if (!els.palette.hidden) return;
  els.palette.hidden = false;
  els.paletteInput.value = '';
  paletteCursor = 0;
  renderPalette();
  els.paletteInput.focus();
}

function closePalette() {
  els.palette.hidden = true;
  els.paletteInput.blur();
}

els.findOpen.addEventListener('click', openPalette);
els.paletteInput.addEventListener('input', () => { paletteCursor = 0; renderPalette(); });

els.paletteInput.addEventListener('keydown', (event) => {
  const n = paletteItems.length;
  if (event.key === 'ArrowDown' && n) paletteCursor = (paletteCursor + 1) % n;
  else if (event.key === 'ArrowUp' && n) paletteCursor = (paletteCursor - 1 + n) % n;
  else if (event.key === 'Enter') { takePalette(paletteItems[paletteCursor]); return; }
  else if (event.key === 'Escape') { closePalette(); return; }
  else return;
  event.preventDefault();
  renderPalette();
});

els.paletteBody.addEventListener('click', (event) => {
  const hit = event.target.closest('.palette-hit');
  if (hit) takePalette(paletteItems[Number(hit.dataset.i)]);
});

// A click on the backdrop, not on the box.
els.palette.addEventListener('mousedown', (event) => {
  if (event.target === els.palette) closePalette();
});

// ---------------------------------------------------------------------- a word

let sayTimer = 0;
function say(text, ms = 2600) {
  els.hint.textContent = text;
  els.hint.classList.add('show');
  clearTimeout(sayTimer);
  sayTimer = setTimeout(() => els.hint.classList.remove('show'), ms);
}

// ------------------------------------------------------------------------ boot

dressPage();
dressPlates();
buildQuick();
mountView();
rows = buildList();
els.moves.addEventListener('wheel', markScroll, { passive: true });
els.moves.addEventListener('touchmove', markScroll, { passive: true });
buildTrack();
review = mountPageReview();
mountMomentInvite();
mountForces();
mountCameraMenu();
mountStory();
// 2D / 3D to the masthead and full screen to the bar (`../lean-layout.js`).
mountLeanLayout();
mountBoardTools();
mountLook();
buildLook();
lessonReady = true;
if (pendingLens) applyLens(pendingLens);
applyStageSize(true);

// The camera, before the first frame is drawn: a name, three numbers, or `auto`
// for the video's own. `?video=1` implies auto — it means "render what the upload
// renders", and the upload's camera is part of that.
boardCam.setView(videoMode ? (app.get('view') || 'auto') : (app.get('view') || 'angled'), { snap: true });

const opening = plyFromUrl();
if (opening !== null) {
  // Played rather than jumped to: somebody who followed a link to 18.Bd6 arrives
  // and watches the bishop go to d6, which is the whole reason the link names a
  // move.
  //
  // After the first paint, though, and for the engine's own reason (see the two
  // frames of delay in `commitPending`): the position is drawn, then the move
  // plays. Started here instead, the first tick of the beat measures the boot's
  // own last few hundred milliseconds — laying out forty-five rows and getting the
  // first real frame onto the GPU — and a quarter of a second of the move has gone
  // by before anything is on screen.
  seek(frameOfPly(opening));
  follow(opening);
  requestAnimationFrame(() => requestAnimationFrame(() => goToPly(opening)));
} else {
  seek(0);
}

boot.classList.add('gone');
say('Drag the board to look around');
if (app.get('play') === '1') play();

// Handy from the console, and what scripts/check-player.mjs drives.
window.__player = {
  play, pause, seek, goToPly, plyAt, frameOfPly, setSpeed, setView: boardCam.setView,
  // What the voice would say on a move, and the chapters the game has now.
  narrationLines,
  phases: () => meta207().phases.map((p) => ({ ...p })),
  // Watching with somebody, for `check-together.mjs`: the room this page is in,
  // and the two ends of the protocol it drives.
  get room() { return room; },
  // A lesson, for `check-lesson.mjs`: the room as it described itself, the offers the
  // coach has, and the lens as this page shows it.
  get lesson() { return lesson; },
  get offers() { return offers; },
  get forces() { return engine.params.forces || 'off'; },
  setForces,
  startLesson: () => together?.startLesson(),
  get sharing() { return Boolean(together?.connected); },
  openTogether,
  leaveRoom,
  // The link-builder the palette uses to change game without leaving the room,
  // so the check drives the real one rather than a hand-written copy of it.
  takeRoomTo,
  // `{ maxed, set, toggle }` — the board over the whole screen, from the console
  // as well as from the button. See `chess-app/maximise.js`.
  maximise: full,
  appDefaults: APP_DEFAULTS,
  // The whole view, unrounded — what `state().camera` reports is for reading,
  // this is for working out why a gesture landed where it did.
  view: () => (boardCam.held ? { held: { ...boardCam.held }, want: { ...boardCam.want } } : null),
  state: () => ({
    frame: Math.round(frame),
    rendered,
    playing,
    speed,
    ply: plyAt(rendered),
    tier: TIERS[tierIx].name,
    size: sizing.size(),
    view: boardCam.viewName,
    // What the scene is actually being told, or null when the scene is deciding.
    camera: boardCam.held && {
      az: Math.round(boardCam.held.az * 10) / 10,
      el: Math.round(boardCam.held.el * 10) / 10,
      zoom: Math.round(boardCam.held.zoom * 100) / 100,
    },
  }),
};
